<template>
  <div class="containerGeralInspection">
    <header class="pageHeader">
      <div class="pageHeaderIcon" aria-hidden="true">🌿</div>
      <div class="titleHeader">
        <p class="eyebrow">ATENDIMENTO AGRONÔMICO</p>
        <h1 class="titleMain">
          {{ dataInspection.id ? "Detalhes da visita" : "Nova visita técnica" }}
        </h1>
      </div>
      <span class="statusBadge">{{ dataInspection.status }}</span>
    </header>

    <section class="formSection visitDataSection">
      <div class="sectionHeading">
        <span class="sectionNumber">01</span>
        <div>
          <h2>Dados da visita</h2>
          <p>Identificação, condições e contexto do atendimento</p>
        </div>
      </div>

      <div class="visitGrid visitGridPrimary">
        <Input
          class="fieldDate"
          ref="dataInspecao"
          label="Data da Visita"
          :labelFixed="true"
          type="date"
          :dataInput.sync="dataInspection.data"
        />

        <Input
          class="fieldTime"
          ref="horaInspecao"
          label="Hora"
          :labelFixed="true"
          type="time"
          :dataInput.sync="dataInspection.hora"
        />

        <Input
          class="fieldTemperature"
          ref="temperatura"
          label="Temperatura (°C)"
          :labelFixed="true"
          type="text"
          :dataInput.sync="dataInspection.temperatura"
        />

        <Input
          class="fieldHumidity"
          ref="umidade"
          label="Umidade do Ar"
          :labelFixed="true"
          type="text"
          :dataInput.sync="dataInspection.umidade"
        />

        <InputSelect
          label="Veículo da visita"
          :items="dataVehicles"
          valueField="id"
          textField="placa"
          :dataInput="dataInspection.idVeiculo"
          :dataSelect.sync="dataInspection.idVeiculo"
          :openCreateModal="() => openModals('modalVehicle')"
          ref="refVehicle"
        />
      </div>
      <div class="visitGrid visitGridLocation">
        <div class="referenceField">
          <InputSelect
            label="Funcionário"
            :items="dataEmployees"
            valueField="id"
            textField="nomeFuncionario"
            :dataInput="dataInspection.idFuncionario"
            :dataSelect.sync="dataInspection.idFuncionario"
            :openCreateModal="() => openModals('modalCreateEmployee')"
            ref="refEmployee"
          />
        </div>
        <div class="fieldProfessional referenceField">
          <InputSelect
            label="Responsável Técnico"
            :items="dataTechnicalManagers"
            valueField="id"
            textField="nome"
            :dataInput="dataInspection.idResponsavelTecnico"
            :dataSelect.sync="dataInspection.idResponsavelTecnico"
            :openCreateModal="() => openModals('modalCreateTechnicalManager')"
            @selectedCompletedItem="handleResponsibleSelection"
            ref="refResponsavel"
          />
        </div>
      </div>
      <div class="visitGrid visitGridLocation">
        <div class="fieldCustomer referenceField">
          <div class="referenceMode">
            <button type="button" @click="toggleReferenceMode('customer')">
              {{
                referenceModes.customer === "registered"
                  ? "Digitar novo"
                  : "Usar cadastro"
              }}
            </button>
          </div>
          <InputInfinit
            v-if="referenceModes.customer === 'registered'"
            label="Cliente cadastrado"
            typeFilter="customer"
            :isEdit="dataInspection.idCliente"
            :dataInput.sync="dataInspection.idCliente"
            :onSearch="fillCustomerComboBoxWithPaginate"
            :openCreateModal="() => openModals('modalCreateCustomer')"
            :findAll="createdNewCustomer"
            @selectItem="handleDataCustomers($event)"
          />
          <Input
            v-else
            label="Nome do produtor / cliente"
            :labelFixed="true"
            type="text"
            :dataInput.sync="dataInspection.customerName"
            placeholder="Digite o nome do produtor"
          />
        </div>

        <div class="fieldProperty referenceField">
          <div class="referenceMode">
            <button type="button" @click="toggleReferenceMode('property')">
              {{
                referenceModes.property === "registered"
                  ? "Digitar novo"
                  : "Usar cadastro"
              }}
            </button>
          </div>
          <InputSelect
            v-if="referenceModes.property === 'registered'"
            label="Propriedade cadastrada"
            :items="dataProperties"
            valueField="id"
            textField="nome"
            :enableIconCreate="false"
            :isDisabled="!dataInspection.idCliente"
            :dataInput="dataInspection.idTalhao"
            :dataSelect.sync="dataInspection.idTalhao"
            @selectedCompletedItem="handlePropertySelection"
            ref="refProperty"
          />
          <Input
            v-else
            label="Nome da propriedade"
            :labelFixed="true"
            type="text"
            :dataInput.sync="dataInspection.propertyName"
            placeholder="Digite a propriedade"
          />
        </div>
      </div>

      <DiagnosisNavigator
        :diagnoses="dataInspection.diagnosticos || []"
        :activeId="activeDiagnosisId"
        @select="selectDiagnosis"
        @add="addDiagnosis"
        @duplicate="duplicateDiagnosis"
        @remove="removeDiagnosis"
      />

      <div class="activeDiagnosisBanner">
        <span>Diagnóstico ativo</span>
        <strong>
          {{ activeDiagnosis.nomeTalhao || "Novo talhão" }} ·
          {{ activeDiagnosis.cultureName || "Selecione a cultura" }}
        </strong>
      </div>

      <div class="visitGrid visitGridCrop diagnosisContextGrid">
        <div class="referenceField">
          <div class="referenceMode">
            <button type="button" @click="toggleReferenceMode('talhao')">
              {{
                activeDiagnosis.talhaoMode === "registered"
                  ? "Digitar novo"
                  : "Usar cadastro"
              }}
            </button>
          </div>
          <InputSelect
            v-if="activeDiagnosis.talhaoMode === 'registered'"
            :key="`talhao-${activeDiagnosisId}`"
            label="Talhão cadastrado"
            :items="dataTalhoes"
            valueField="id"
            textField="nome"
            :enableIconCreate="false"
            :isDisabled="!dataInspection.idTalhao"
            :dataInput="activeDiagnosis.talhaoId"
            :dataSelect.sync="activeDiagnosis.talhaoId"
            @selectedCompletedItem="handleTalhaoSelection"
            ref="refTalhao"
          />
          <Input
            v-else
            label="Nome do talhão"
            :labelFixed="true"
            type="text"
            :dataInput.sync="activeDiagnosis.nomeTalhao"
            placeholder="Digite o talhão"
          />
        </div>
        <div class="referenceField">
          <div class="referenceMode">
            <button type="button" @click="toggleReferenceMode('culture')">
              {{
                activeDiagnosis.cultureMode === "registered"
                  ? "Digitar nova"
                  : "Usar cadastro"
              }}
            </button>
          </div>
          <InputInfinit
            v-if="activeDiagnosis.cultureMode === 'registered'"
            :key="`culture-${activeDiagnosisId}`"
            label="Cultura do Compêndio"
            typeFilter="culture"
            :isEdit="activeDiagnosis.culturaSelecionada"
            :dataInput.sync="activeDiagnosis.culturaSelecionada"
            :onSearch="fillCulturesWithPaginate"
            :enableIconCreate="false"
            :filterIsEnable="false"
            :noPadding="true"
            @selectItem="handleCultureSelection"
          />
          <Input
            v-else
            label="Nome da cultura"
            :labelFixed="true"
            type="text"
            :dataInput.sync="activeDiagnosis.cultureName"
            placeholder="Digite uma cultura"
          />
        </div>

        <Input
          label="Variedade / Cultivar"
          :labelFixed="true"
          type="text"
          :dataInput.sync="activeDiagnosis.variedade"
          placeholder="Ex: Brasmax Bônus"
        />

        <Input
          label="Estádio Fenológico"
          :labelFixed="true"
          type="text"
          :dataInput.sync="activeDiagnosis.estadioFenologico"
          placeholder="Ex: R1 - Início do florescimento"
        />

        <InputSelect
          label="Umidade do Solo"
          :items="soilMoistureOptions"
          valueField="id"
          textField="text"
          :dataInput="activeDiagnosis.umidadeSolo"
          :dataSelect.sync="activeDiagnosis.umidadeSolo"
        />
      </div>

      <div class="visitContext">
        <label>
          <span>Motivo da visita</span>
          <input
            v-model.trim="dataInspection.reason"
            placeholder="Ex: monitoramento preventivo"
          />
        </label>
        <label>
          <span>Situação encontrada</span>
          <input
            v-model.trim="dataInspection.situation"
            placeholder="Resumo da condição observada"
          />
        </label>
        <button class="btnGps" type="button" @click="capturarLocalizacao">
          <span v-if="dataInspection.coordenadas.lat">
            📍 {{ dataInspection.coordenadas.lat.toFixed(5) }},
            {{ dataInspection.coordenadas.lng.toFixed(5) }}
          </span>
          <span v-else>📍 Capturar localização</span>
        </button>
      </div>
    </section>

    <div class="sectionDivider">
      <span>02</span>
      <div>
        <b>Inspeção fitossanitária</b
        ><small>Monitoramento realizado durante a visita</small>
      </div>
    </div>

    <div class="contentChecklist">
      <div class="mipHeading">
        <div class="sectionTitleAgro">
          <p class="obsGeneral">Amostragem Fitossanitária (MIP)</p>
          <p class="observacoes">
            Registre o método e consolide a pressão observada nos pontos
            amostrais.
          </p>
        </div>
        <span class="mipTargetCount">
          {{ totalMipTargets }} alvos monitorados
        </span>
      </div>

      <div class="samplingPlan">
        <label>
          <span>Método de amostragem</span>
          <select v-model="activeDiagnosis.mipSampling.method">
            <option value="">Selecione o método</option>
            <option v-for="method in samplingMethods" :key="method">
              {{ method }}
            </option>
          </select>
        </label>
        <label>
          <span>Nº de pontos amostrais</span>
          <input
            v-model.number="activeDiagnosis.mipSampling.samplePoints"
            type="number"
            min="0"
            placeholder="Ex: 10"
          />
        </label>
        <label>
          <span>Área avaliada (ha)</span>
          <input
            v-model.number="activeDiagnosis.mipSampling.assessedArea"
            type="number"
            min="0"
            step="0.01"
            placeholder="Ex: 25"
          />
        </label>
        <label>
          <span>Distribuição dos pontos</span>
          <select v-model="activeDiagnosis.mipSampling.distribution">
            <option value="">Selecione</option>
            <option v-for="item in samplingDistributions" :key="item">
              {{ item }}
            </option>
          </select>
        </label>
        <label class="samplingNotes">
          <span>Observações da amostragem</span>
          <input
            v-model.trim="activeDiagnosis.mipSampling.notes"
            placeholder="Condições ou particularidades dos pontos"
          />
        </label>
      </div>

      <MipDiagnosisRows
        :diagnosis="activeDiagnosis"
        :catalogs="mipTargetCatalog"
        :infestation-levels="niveisInfestacao"
      />
    </div>

    <div class="sectionDivider">
      <span>03</span>
      <div>
        <b>Registro técnico</b
        ><small>Evidências, diagnóstico e recomendação</small>
      </div>
    </div>

    <div class="contentItens">
      <b-row class="mt-2 col-sm-12 col-md-12 col-lg-12 col-xl-12">
        <div class="col-sm-12 col-md-12 col-lg-12 col-xl-12">
          <div class="fieldHeading">
            <p class="obsGeneral">Registro Fotográfico</p>
            <p class="observacoes">
              (Anexe fotos das pragas, doenças ou do estado geral da lavoura)
            </p>
          </div>
          <input
            type="file"
            multiple
            accept="image/*"
            @change="handleFileUpload"
            class="inputFileAgro"
          />

          <div
            class="previewContainer mt-2"
            v-if="activeDiagnosisPhotos.length > 0"
          >
            <div
              class="previewImg"
              v-for="(foto, index) in activeDiagnosisPhotos"
              :key="index"
            >
              <img
                class="imgBox"
                v-if="foto.url && !isRawFile(foto)"
                :src="foto.url"
                :alt="foto.name || 'Anexo'"
              />
              <img
                class="imgBox"
                v-else-if="isRawFile(foto)"
                :src="getObjectURL(foto)"
                :alt="foto.name"
              />
              <span>{{ foto.name || foto || `Arquivo ${index + 1}` }}</span>
              <button
                class="photoCoordBtn"
                :class="{ active: photoLocationTarget === foto }"
                :title="
                  hasPhotoCoords(foto)
                    ? 'Editar coordenadas'
                    : 'Incluir coordenadas'
                "
                @click.prevent="openPhotoLocation(foto)"
              >
                📍
              </button>
              <span v-if="hasPhotoCoords(foto)" class="photoCoordBadge">
                {{ Number(foto._latitude ?? foto.latitude).toFixed(4) }},
                {{ Number(foto._longitude ?? foto.longitude).toFixed(4) }}
              </span>
            </div>
          </div>

          <PhotoLocationPicker
            :visible="!!photoLocationTarget"
            :latitude="photoLocationLat"
            :longitude="photoLocationLng"
            @location-selected="onPhotoLocationSelected"
            @close="closePhotoLocation"
          />
        </div>
      </b-row>

      <b-row class="mt-4 col-sm-12 col-md-12 col-lg-12 col-xl-12">
        <div class="col-sm-12 col-md-12 col-lg-12 col-xl-12">
          <div class="fieldHeading">
            <p class="obsGeneral">Registro de Áudio</p>
            <p class="observacoes">
              (Grave e anexe observações de campo em áudio)
            </p>
          </div>
          <!-- Transcrição em standby: contexto e eventos foram mantidos no código comentado. -->
          <AudioRecorder
            :key="`audio-${activeDiagnosisId}`"
            :recordings="activeDiagnosisAudios"
            :inspection-id="dataInspection.id"
            @completed="handleAudioCompleted"
            @remove="removeAudio"
          />
        </div>
      </b-row>

      <b-row class="mt-4 col-sm-12 col-md-12 col-lg-12 col-xl-12">
        <div class="col-sm-12 col-md-12 col-lg-12 col-xl-12">
          <div class="fieldHeading">
            <p class="obsGeneral">Diagnóstico e Sintomatologia Visual</p>
            <p class="observacoes">
              (Anote aqui padrões de reboleiras, manchas foliares ou anomalias
              climáticas)
            </p>
          </div>
          <textarea
            class="inputTextAreaAgro"
            rows="3"
            v-model="activeDiagnosis.dadosAdicionais"
            placeholder="Ex: Identificado início de reboleira de Capim Amargoso na face norte do talhão..."
          />
        </div>

        <div class="mt-4 col-sm-12 col-md-12 col-lg-12 col-xl-12">
          <div class="fieldHeading fieldHeadingWithAction">
            <div class="fieldHeadingCopy">
              <p class="obsGeneral">
                Recomendação Técnica Agronômica (Prescrição)
              </p>
              <p class="observacoes">
                (Base para a emissão do Receituário Agronômico)
              </p>
            </div>

            <button
              class="btnAIAssist"
              :disabled="loadingAgrotoxics"
              @click.prevent="consultarAssistenteIA"
            >
              ✨ Consultar defensivos em estoque
            </button>
          </div>
          <textarea
            class="inputTextAreaAgro"
            rows="3"
            v-model="activeDiagnosis.recomendacaoManejo"
            placeholder="Ex: Recomenda-se aplicação preventiva de fungicida sistêmico..."
          />

          <div
            v-if="activeDiagnosis.recommendedProducts.length"
            class="recommendedProductsPanel"
          >
            <div class="recommendedProductsHeader">
              <div>
                <p class="obsGeneral">Produtos vinculados ao diagnóstico</p>
                <p class="observacoes">
                  Estes itens serão levados somente para a receita da cultura
                  {{ activeDiagnosis.cultureName }}.
                </p>
              </div>
              <span class="recommendedProductsCount">
                {{ activeDiagnosis.recommendedProducts.length }} produto(s)
              </span>
            </div>
            <div
              v-for="product in activeDiagnosis.recommendedProducts"
              :key="product.id"
              class="recommendedProductRow"
            >
              <div>
                <strong>{{ product.pesticideName }}</strong>
                <span>
                  {{ product.pesticideRegister }} · {{ product.targetName }} ·
                  {{
                    product.source === "stock"
                      ? "Em estoque"
                      : "Somente Compêndio"
                  }}
                </span>
                <small>
                  {{ product.packageDescription || "Sem embalagem de estoque" }}
                  <template v-if="product.batch">
                    · Lote {{ product.batch }}</template
                  >
                  · Aplicação
                  {{ formatApplicationDate(product.applicationDate) }}
                </small>
              </div>
              <button
                type="button"
                class="removeRecommendedProduct"
                @click="removeRecommendedProduct(product.id)"
              >
                Remover
              </button>
            </div>
          </div>

          <div
            v-if="
              loadingAgrotoxics ||
              hasSearchedAgrotoxics ||
              Object.keys(agrotoxicSuggestions).length
            "
            class="agrotoxicSuggestionsPanel"
          >
            <div class="suggestionsPanelHeader">
              <div>
                <p class="obsGeneral">Sugestão de Defensivos</p>
                <p class="observacoes">
                  {{ suggestionsSubtitle }}
                </p>
              </div>
              <div v-if="!loadingAgrotoxics" class="suggestionHeaderActions">
                <span class="badgeCompendio">
                  {{
                    suggestionSource === "stock" ? "Em estoque" : "Compêndio"
                  }}
                </span>
                <button
                  v-if="suggestionSource === 'stock'"
                  type="button"
                  class="btnSearchCompendium"
                  @click="pesquisarNoCompendio"
                >
                  Ver todos no Compêndio
                </button>
              </div>
              <span v-else class="badgeCompendio badgeLoading"
                >Buscando...</span
              >
            </div>

            <div v-if="loadingAgrotoxics" class="suggestionsLoadingState">
              <div class="loadingSpinner"></div>
              <span>Verificando indicações para a cultura e os alvos...</span>
            </div>

            <template v-else>
              <div class="suggestionsSearchBar">
                <input
                  v-model.trim="searchAgrotoxics"
                  type="text"
                  class="inputSearchAgro"
                  placeholder="Buscar por nome ou registro do agrotóxico..."
                />
                <span v-if="searchAgrotoxics" class="searchResultCount">
                  {{ allFilteredCount() }} resultado(s)
                </span>
              </div>

              <div class="suggestionsScrollArea">
                <section
                  v-for="diagnosis in searchableDiagnoses"
                  :key="diagnosis.id"
                  class="suggestionDiagnosisSection"
                >
                  <div class="suggestionDiagnosisHeader">
                    <strong>{{ diagnosis.nomeTalhao || "Diagnóstico" }}</strong>
                    <span>{{ diagnosis.cultureName }}</span>
                  </div>
                  <div
                    v-for="target in diagnosisTargets(diagnosis)"
                    :key="target.id"
                  >
                    <div
                      v-if="targetSuggestions(target.id).length"
                      class="suggestionGroup"
                    >
                      <div class="suggestionGroupHeader">
                        <div class="suggestionGroupTitle">
                          <span class="suggestionTargetIcon">
                            {{ targetIcon(target.targetType) }}
                          </span>
                          <span class="suggestionTargetName">{{
                            target.nome
                          }}</span>
                          <small
                            v-if="target.nomeCientifico"
                            class="suggestionTargetSci"
                            >{{ target.nomeCientifico }}</small
                          >
                        </div>
                        <span class="suggestionGroupCount">{{
                          targetSuggestions(target.id).length
                        }}</span>
                      </div>

                      <div class="suggestionGrid">
                        <div
                          v-for="agro in targetSuggestions(target.id)"
                          :key="agro.id"
                          class="suggestionCard"
                        >
                          <div class="suggestionCardHeader">
                            <span class="agrotoxicoReg">{{
                              agro.registro
                            }}</span>
                            <span
                              class="agrotoxicoName"
                              :title="agro.nomeComum"
                              >{{ agro.nomeComum }}</span
                            >
                            <span
                              v-if="agro.emEstoque"
                              class="stockAvailability"
                            >
                              Disponível em estoque
                            </span>
                            <span v-else class="compendiumOnlyAvailability">
                              Somente Compêndio
                            </span>
                          </div>

                          <label
                            v-if="
                              agro.stockProducts && agro.stockProducts.length
                            "
                            class="stockProductSelector"
                          >
                            <span>Produto do estoque</span>
                            <select
                              :value="selectedStockProductId(target.id, agro)"
                              @change="
                                setSelectedStockProduct(
                                  target.id,
                                  agro.id,
                                  $event.target.value
                                )
                              "
                            >
                              <option
                                v-for="stockProduct in agro.stockProducts"
                                :key="stockProduct.id"
                                :value="stockProduct.id"
                              >
                                {{ stockProductLabel(stockProduct) }}
                              </option>
                            </select>
                          </label>
                          <p v-else class="outOfStockNotice">
                            Pode ser vinculado à inspeção, mas não será enviado
                            para a receita.
                          </p>
                          <label
                            v-if="applicationMethods(agro.id).length"
                            class="stockProductSelector"
                          >
                            <span>Modalidade de aplicação</span>
                            <select
                              :value="
                                selectedApplicationMethodId(target.id, agro.id)
                              "
                              @change="
                                setSelectedApplicationMethod(
                                  target.id,
                                  agro.id,
                                  $event.target.value
                                )
                              "
                            >
                              <option value="" disabled>Selecione</option>
                              <option
                                v-for="method in applicationMethods(agro.id)"
                                :key="method.id"
                                :value="method.id"
                              >
                                {{ method.name }}
                              </option>
                            </select>
                          </label>
                          <p v-else class="outOfStockNotice">
                            Modalidade de aplicação não cadastrada no Compêndio.
                          </p>

                          <div
                            v-if="agrotoxicDetails[target.id + '-' + agro.id]"
                            class="suggestionCardDetails"
                          >
                            <span
                              v-if="
                                agrotoxicDetails[target.id + '-' + agro.id]
                                  .doses &&
                                agrotoxicDetails[target.id + '-' + agro.id]
                                  .doses.length
                              "
                              class="detailChip"
                            >
                              Dose:
                              {{
                                agrotoxicDetails[target.id + "-" + agro.id]
                                  .doses[0].doseMinima
                              }}
                              a
                              {{
                                agrotoxicDetails[target.id + "-" + agro.id]
                                  .doses[0].doseMaxima
                              }}
                              {{
                                agrotoxicDetails[target.id + "-" + agro.id]
                                  .doses[0].unidadeDose
                              }}
                            </span>
                            <span
                              v-if="
                                agrotoxicDetails[target.id + '-' + agro.id]
                                  .intervalos &&
                                agrotoxicDetails[target.id + '-' + agro.id]
                                  .intervalos.length &&
                                agrotoxicDetails[target.id + '-' + agro.id]
                                  .intervalos[0].seguranca
                              "
                              class="detailChip"
                            >
                              Carência:
                              {{
                                agrotoxicDetails[target.id + "-" + agro.id]
                                  .intervalos[0].seguranca
                              }}
                              dias
                            </span>
                          </div>

                          <div class="suggestionCardFooter">
                            <input
                              type="date"
                              class="inputDateCompact"
                              :value="
                                agrotoxicDates[target.id + '-' + agro.id] || ''
                              "
                              @input="
                                setAgrotoxicDate(
                                  target.id,
                                  agro.id,
                                  $event.target.value
                                )
                              "
                            />
                            <button
                              type="button"
                              class="btnAddSuggestion"
                              :disabled="
                                !agrotoxicDates[target.id + '-' + agro.id] ||
                                !selectedApplicationMethodId(
                                  target.id,
                                  agro.id
                                ) ||
                                loadingAgrotoxicDetails[
                                  target.id + '-' + agro.id
                                ]
                              "
                              @click="
                                addSuggestionToRecommendation(
                                  agro,
                                  target,
                                  diagnosis
                                )
                              "
                            >
                              {{
                                loadingAgrotoxicDetails[
                                  target.id + "-" + agro.id
                                ]
                                  ? "Carregando..."
                                  : "+ Adicionar"
                              }}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="suggestionTargetEmpty">
                      <strong>{{ target.nome }}</strong>
                      <span>
                        {{
                          suggestionSource === "stock"
                            ? "Nenhum produto compatível em estoque para este alvo."
                            : "Nenhum produto encontrado no Compêndio para este alvo."
                        }}
                      </span>
                    </div>
                  </div>
                </section>

                <div
                  v-if="
                    hasSearchedAgrotoxics &&
                    !Object.keys(agrotoxicSuggestions).length &&
                    !loadingAgrotoxics
                  "
                  class="suggestionsEmptyState"
                >
                  <template v-if="suggestionSource === 'stock'">
                    <p>
                      Nenhum defensivo compatível foi encontrado no estoque para
                      esta cultura e diagnóstico.
                    </p>
                    <small v-if="unresolvedAgrotoxicTargets">
                      {{ unresolvedAgrotoxicTargets }} alvo(s) não possuem
                      correspondência no Compêndio.
                    </small>
                    <small v-if="failedAgrotoxicTargets">
                      A consulta falhou para {{ failedAgrotoxicTargets }}
                      alvo(s).
                    </small>
                    <button
                      type="button"
                      class="btnSearchCompendium"
                      @click="pesquisarNoCompendio"
                    >
                      Pesquisar no Compêndio
                    </button>
                  </template>
                  <template v-else-if="failedAgrotoxicTargets">
                    <p>
                      Não foi possível concluir a consulta de
                      {{ failedAgrotoxicTargets }} alvo(s) no Compêndio.
                    </p>
                    <button
                      type="button"
                      class="btnSearchCompendium"
                      @click="retryPesticideSearch"
                    >
                      Tentar novamente
                    </button>
                  </template>
                  <template v-else>
                    <p>
                      Nenhum defensivo foi encontrado no Compêndio para os alvos
                      diagnosticados.
                    </p>
                    <small v-if="unresolvedAgrotoxicTargets">
                      {{ unresolvedAgrotoxicTargets }} alvo(s) não possuem
                      correspondência no Compêndio.
                    </small>
                  </template>
                </div>

                <div
                  v-if="
                    searchAgrotoxics &&
                    allFilteredCount() === 0 &&
                    Object.keys(agrotoxicSuggestions).length
                  "
                  class="suggestionsEmptyState"
                >
                  Nenhum agrotóxico encontrado para "{{ searchAgrotoxics }}".
                </div>
              </div>
            </template>
          </div>
        </div>
      </b-row>

      <b-row class="mt-4 mb-4 col-sm-12 col-md-12 col-lg-12 col-xl-12">
        <div class="col-sm-12 workflowPanel">
          <div class="workflowHeader">
            <div>
              <p class="obsGeneral">Providências e pendências</p>
              <p class="observacoes">
                Registre ações, responsáveis, prazos e acompanhe a resolução.
              </p>
            </div>
            <button
              class="btnSecondary"
              type="button"
              @click="addPendingAction"
            >
              + Adicionar providência
            </button>
          </div>
          <div
            v-if="!dataInspection.pendingActions.length"
            class="emptyPendingState"
          >
            Nenhuma providência adicionada até o momento.
          </div>
          <div
            v-for="(action, index) in dataInspection.pendingActions"
            :key="action.id"
            class="pendingRow"
          >
            <label class="pendingDescription">
              <span>Ação necessária</span>
              <input
                v-model.trim="action.description"
                placeholder="Descreva a providência"
              />
            </label>
            <label>
              <span>Responsável</span>
              <input
                v-model.trim="action.responsible"
                placeholder="Nome do responsável"
              />
            </label>
            <label>
              <span>Prazo</span>
              <input v-model="action.deadline" type="date" />
            </label>
            <label>
              <span>Status</span>
              <select
                v-model="action.status"
                @change="registerPendingStatus(action)"
              >
                <option v-for="status in pendingStatuses" :key="status">
                  {{ status }}
                </option>
              </select>
            </label>
            <button
              type="button"
              class="removeAction"
              @click="removePendingAction(index)"
              aria-label="Remover providência"
            >
              ×
            </button>
            <small v-if="action.completedAt"
              >Concluída em {{ formatAuditDate(action.completedAt) }}</small
            >
          </div>
        </div>

        <div class="col-sm-12 workflowPanel mt-3">
          <p class="obsGeneral">Receituário agronômico</p>
          <p class="observacoes">
            A receita existente será apenas referenciada e não duplicada.
          </p>
          <input
            class="recipeInput"
            :value="activeDiagnosis.recipeId"
            readonly
            placeholder="Identificador da receita existente"
          />
        </div>
        <div
          class="col-sm-12 col-md-12 col-lg-12 col-xl-12 contentButtonsActions"
        >
          <button class="btnsBudget btnBack" @click="$emit('close')">
            Voltar ao Menu
          </button>
          <div class="primaryActions">
            <button
              v-if="dataInspection.id"
              type="button"
              class="btnsBudget btnPrintInspection"
              @click="printInspection"
            >
              🖨 Imprimir inspeção
            </button>
            <button class="btnsBudget btnSave" :disabled="isSavingInspection || isConcluding" @click="saveInspection(false)">
              {{
                isSavingInspection
                  ? "Salvando..."
                  : dataInspection.id
                  ? "Atualizar atendimento"
                  : "Salvar atendimento"
              }}
            </button>
            <button
              v-if="
                dataInspection.status === 'Concluído' &&
                remainingRecipeCultureOptions.length
              "
              class="btnsBudget btnConfirmAgro"
              @click="openRecipePrompt"
            >
              Gerar receita por cultura
            </button>
            <button
              v-if="
                !['Concluído', 'Cancelado'].includes(dataInspection.status)
              "
              class="btnsBudget btnConfirmAgro"
              @click="openConclusion"
            >
              ✓ Concluir atendimento
            </button>
          </div>
        </div>
      </b-row>
    </div>

    <ModalCreateCustomer
      @getAllNewCustomer="handleCreatedNewCustomer"
      :idCustomerFromSearch="dataInspection.idCliente"
    />
    <ModalCreateEmployees
      @getAllNewEmployee="loadEmployees"
      :idEmployeeFromSearch="dataInspection.idFuncionario"
    />
    <ModalCreateResponsible
      @getAllResponsibles="loadTechnicalManagers"
      :idResponsibleFromSearch="dataInspection.idResponsavelTecnico"
    />
    <ModalCreateVehicle @getNewVehicle="loadVehicles" />
    <b-modal
      id="conclude-attendance-modal"
      v-model="showConclusion"
      title="Concluir atendimento"
      hide-footer
    >
      <label class="conclusionQuestion"
        >O problema foi resolvido?
        <select v-model="conclusion.resolved">
          <option :value="true">Sim</option>
          <option :value="false">Não</option>
        </select>
      </label>
      <label class="conclusionQuestion"
        >Deseja agendar acompanhamento?
        <select v-model="conclusion.followUp">
          <option :value="false">Não</option>
          <option :value="true">Sim</option>
        </select>
      </label>
      <div v-if="conclusion.followUp" class="followUpOptions">
        <button
          v-for="days in [7, 15, 30]"
          :key="days"
          @click="setFollowUp(days)"
        >
          {{ days }} dias
        </button>
        <input
          type="date"
          class="input borderInput"
          v-model="conclusion.followUpDate"
        />
      </div>
      <button
        class="btnsBudget btnConfirmAgro concludeButton"
        :disabled="isConcluding || isSavingInspection"
        @click="concludeAttendance"
      >
        {{ isConcluding ? "Concluindo..." : "Confirmar conclusão" }}
      </button>
    </b-modal>
    <b-modal
      id="generate-prescription-modal"
      v-model="showRecipePrompt"
      title="Gerar receituário agronômico"
      hide-footer
      centered
    >
      <p class="recipePromptText">
        Selecione a cultura. Os dados da visita e os produtos recomendados em
        estoque serão preparados na receita para você completar e gerar.
      </p>
      <label class="conclusionQuestion">
        Cultura que originará a receita
        <select v-model="recipeCultureId">
          <option
            v-for="culture in recipeCultureOptions"
            :key="culture.id"
            :value="culture.id"
            :disabled="culture.hasRecipe"
          >
            {{ culture.name }} · {{ culture.diagnosisCount }} diagnóstico(s) ·
            {{ culture.stockProductCount }} em estoque
            <template v-if="culture.compendiumProductCount">
              · {{ culture.compendiumProductCount }} somente no Compêndio
            </template>
            <template v-if="culture.hasRecipe"> · receita já gerada</template>
          </option>
        </select>
      </label>
      <div class="recipePromptActions">
        <button
          type="button"
          class="btnsBudget btnBack"
          @click="showRecipePrompt = false"
        >
          Agora não
        </button>
        <button
          type="button"
          class="btnsBudget btnConfirmAgro"
          :disabled="!selectedRecipeCulture || selectedRecipeCulture.hasRecipe"
          @click="startPrescription"
        >
          Gerar receita
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import moment from "moment";

import ModalCreateCustomer from "../Modals/ModalCreateCustomer.vue";
import ModalCreateEmployees from "../Modals/ModalCreateEmployees.vue";
import ModalCreateResponsible from "../Modals/ModalCreateResponsible.vue";
import ModalCreateVehicle from "../Modals/ModalCreateVehicle.vue";
import DiagnosisNavigator from "./DiagnosisNavigator.vue";
import MipDiagnosisRows from "./MipDiagnosisRows.vue";
import PhotoLocationPicker from "./PhotoLocationPicker.vue";
import AudioRecorder from "../AudioRecorder.vue";
import {
  createInspectionDiagnosis,
  isAgendaMetadata,
  normalizeInspectionDiagnoses,
} from "./inspectionDiagnosis";
import {
  createInspectionRecommendation,
} from "./inspectionRecommendation";
import {
  buildPrescriptionDraftFromInspection,
  inspectionPrescriptionCultureOptions,
} from "./inspectionPrescription";
import InputSelect from "../UI/CustomInputs/InputSelect.vue";
import Input from "../UI/CustomInputs/Input.vue";
import InputInfinit from "../UI/CustomInputs/InputInfinit.vue";
import serviceCustomer from "../../services/serviceCustomer.js";
import serviceEmployee from "../../services/serviceEmployees.js";
import serviceResponsible from "../../services/serviceResponsible.js";
import serviceCustomerProperty from "../../services/serviceCustomerProperty.js";
import serviceCompendio from "../../services/serviceCompendio.js";
import serviceInspection from "../../services/serviceInspection.js";
import { saveCommitment as apiSaveCommitment } from "../../services/agronomicAgenda";
import serviceTalhao from "../../services/serviceTalhao.js";
import serviceProducts from "../../services/serviceProducts.js";
import serviceVehicle from "../../services/serviceVehicle.js";

export default {
  components: {
    InputSelect,
    Input,
    InputInfinit,
    ModalCreateCustomer,
    ModalCreateEmployees,
    ModalCreateResponsible,
    ModalCreateVehicle,
    DiagnosisNavigator,
    MipDiagnosisRows,
    PhotoLocationPicker,
    AudioRecorder,
  },
  data() {
    return {
      dataInspection: {
        id: "",
        idCliente: "",
        idFuncionario: "",
        idResponsavelTecnico: "",
        idVeiculo: "",
        idTalhao: "",
        customerName: "",
        professionalName: "",
        propertyName: "",
        culturaSelecionada: "",
        cultureName: "",
        variedade: "",
        estadioFenologico: "",
        umidadeSolo: "",
        data: moment().format("YYYY-MM-DD"),
        hora: moment().format("HH:mm"),
        temperatura: "",
        nomeTalhao: "",
        umidade: "",
        coordenadas: { lat: null, lng: null },
        fotos: [],
        dadosAdicionais: "",
        recomendacaoManejo: "",
        commitmentId: null,
        reason: "",
        situation: "",
        status: "Aberto",
        recipeId: "",
        pendingActions: [],
        mipSampling: {
          method: "Pano de batida e inspeção visual",
          samplePoints: null,
          assessedArea: null,
          distribution: "Zigue-zague",
          notes: "",
        },
        alvosPragas: [],
        alvosDoencas: [],
        alvosDaninhas: [],
      },
      activeDiagnosisId: "",
      diagnosisPlaceholder: createInspectionDiagnosis(),
      niveisInfestacao: ["Ausente", "Baixa", "Moderada", "Alta"],
      samplingMethods: [
        "Pano de batida e inspeção visual",
        "Inspeção visual de plantas",
        "Armadilhas e inspeção visual",
        "Contagem por metro linear",
        "Quadrado de amostragem",
        "Caminhamento técnico",
      ],
      samplingDistributions: [
        "Zigue-zague",
        "Diagonal",
        "Grade regular",
        "Pontos georreferenciados",
        "Bordadura e interior",
      ],
      mipTargetCatalog: {
        praga: [
          {
            id: "praga-lagarta-falsa-medideira",
            nome: "Lagarta-falsa-medideira",
            nomeCientifico: "Chrysodeixis includens",
            unidade: "lagartas/pano",
          },
          {
            id: "praga-percevejo-marrom",
            nome: "Percevejo-marrom",
            nomeCientifico: "Euschistus heros",
            unidade: "percevejos/pano",
          },
          {
            id: "praga-mosca-branca",
            nome: "Mosca-branca",
            nomeCientifico: "Bemisia tabaci",
            unidade: "ninfas/folíolo",
          },
          {
            id: "praga-lagarta-cartucho",
            nome: "Lagarta-do-cartucho",
            nomeCientifico: "Spodoptera frugiperda",
            unidade: "lagartas/planta",
          },
          {
            id: "praga-cigarrinha-milho",
            nome: "Cigarrinha-do-milho",
            nomeCientifico: "Dalbulus maidis",
            unidade: "insetos/planta",
          },
          {
            id: "praga-pulgao",
            nome: "Pulgões",
            nomeCientifico: "Aphididae",
            unidade: "colônias/planta",
          },
          {
            id: "praga-tripes",
            nome: "Tripes",
            nomeCientifico: "Thysanoptera",
            unidade: "insetos/folha",
          },
          {
            id: "praga-acaros",
            nome: "Ácaros",
            nomeCientifico: "Acari",
            unidade: "ácaros/folíolo",
          },
        ],
        doenca: [
          {
            id: "doenca-ferrugem-asiatica",
            nome: "Ferrugem-asiática",
            nomeCientifico: "Phakopsora pachyrhizi",
          },
          {
            id: "doenca-mancha-alvo",
            nome: "Mancha-alvo",
            nomeCientifico: "Corynespora cassiicola",
          },
          {
            id: "doenca-antracnose",
            nome: "Antracnose",
            nomeCientifico: "Colletotrichum truncatum",
          },
          {
            id: "doenca-oidio",
            nome: "Oídio",
            nomeCientifico: "Erysiphe spp.",
          },
          {
            id: "doenca-mildio",
            nome: "Míldio",
            nomeCientifico: "Peronosporaceae",
          },
          {
            id: "doenca-cercosporiose",
            nome: "Cercosporiose",
            nomeCientifico: "Cercospora spp.",
          },
          {
            id: "doenca-manchas-foliares",
            nome: "Manchas foliares",
            nomeCientifico: "Complexo de patógenos",
          },
          {
            id: "doenca-podridao-raiz",
            nome: "Podridões radiculares",
            nomeCientifico: "Complexo de patógenos",
          },
        ],
        daninha: [
          {
            id: "daninha-capim-amargoso",
            nome: "Capim-amargoso",
            nomeCientifico: "Digitaria insularis",
          },
          { id: "daninha-buva", nome: "Buva", nomeCientifico: "Conyza spp." },
          {
            id: "daninha-caruru",
            nome: "Caruru",
            nomeCientifico: "Amaranthus spp.",
          },
          {
            id: "daninha-capim-pe-galinha",
            nome: "Capim-pé-de-galinha",
            nomeCientifico: "Eleusine indica",
          },
          {
            id: "daninha-capim-colchao",
            nome: "Capim-colchão",
            nomeCientifico: "Digitaria spp.",
          },
          {
            id: "daninha-trapoeraba",
            nome: "Trapoeraba",
            nomeCientifico: "Commelina spp.",
          },
          {
            id: "daninha-corda-viola",
            nome: "Corda-de-viola",
            nomeCientifico: "Ipomoea spp.",
          },
          {
            id: "daninha-leiteiro",
            nome: "Leiteiro",
            nomeCientifico: "Euphorbia heterophylla",
          },
        ],
      },
      soilMoistureOptions: [
        { id: "1", text: "Seco (Déficit Hídrico)" },
        { id: "2", text: "Adequado (Capacidade de Campo)" },
        { id: "3", text: "Encharcado" },
      ],
      dataTechnicalManagers: [],
      dataEmployees: [],
      dataVehicles: [],
      dataProperties: [],
      dataTalhoes: [],
      referenceModes: {
        customer: "registered",
        property: "registered",
        culture: "registered",
        talhao: "registered",
      },
      createdNewCustomer: false,
      pendingStatuses: [
        "Pendente",
        "Em andamento",
        "Resolvido",
        "Não resolvido",
        "Cancelado",
      ],
      agrotoxicSuggestions: {},
      agrotoxicDetails: {},
      agrotoxicDates: {},
      agrotoxicStockProductIds: {},
      agrotoxicApplicationMethods: {},
      agrotoxicApplicationMethodIds: {},
      agrotoxicProblemIds: {},
      loadingAgrotoxicDetails: {},
      agrotoxicSearchCache: {},
      pesticidesInStock: [],
      compendiumProblemCache: {},
      hasSearchedAgrotoxics: false,
      failedAgrotoxicTargets: 0,
      unresolvedAgrotoxicTargets: 0,
      suggestionSource: "stock",
      agrotoxicSearchRequestId: 0,
      searchAgrotoxics: "",
      loadingAgrotoxics: false,
      photoLocationTarget: null,
      photoLocationLat: null,
      photoLocationLng: null,
      showConclusion: false,
      showRecipePrompt: false,
      isSavingInspection: false,
      isConcluding: false,
      recipeCultureId: "",
      conclusion: { resolved: true, followUp: false, followUpDate: "" },
    };
  },
  methods: {
    ...mapMutations(["SET_DATA_SEARCH_FROM_SALE", "SET_SALE_ID"]),

    initializeDiagnoses() {
      const diagnoses = normalizeInspectionDiagnoses(this.dataInspection);
      this.$set(this.dataInspection, "diagnosticos", diagnoses);
      this.activeDiagnosisId = diagnoses[0].id;
    },

    selectDiagnosis(id) {
      this.activeDiagnosisId = id;
    },

    addDiagnosis() {
      if (this.dataInspection.diagnosticos.length >= 30) {
        this.$toast.open({
          message: "Uma visita pode ter no máximo 30 diagnósticos",
          type: "warning",
        });
        return;
      }
      const diagnosis = createInspectionDiagnosis();
      this.dataInspection.diagnosticos.push(diagnosis);
      this.selectDiagnosis(diagnosis.id);
    },

    duplicateDiagnosis(id) {
      const source = this.dataInspection.diagnosticos.find(
        (item) => item.id === id
      );
      if (!source) return;
      const diagnosis = createInspectionDiagnosis({
        ...source,
        id: null,
        nomeTalhao: "",
        talhaoId: "",
        recipeId: "",
        recommendedProducts: [],
        alvosPragas: source.alvosPragas.map((item) => ({ ...item, id: null })),
        alvosDoencas: source.alvosDoencas.map((item) => ({
          ...item,
          id: null,
        })),
        alvosDaninhas: source.alvosDaninhas.map((item) => ({
          ...item,
          id: null,
        })),
      });
      this.dataInspection.diagnosticos.push(diagnosis);
      this.selectDiagnosis(diagnosis.id);
    },

    async removeDiagnosis(id) {
      if (this.dataInspection.diagnosticos.length === 1) return;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        "Remover este diagnóstico, suas fotos e seus áudios da visita?",
        {
          title: "Remover diagnóstico",
          okTitle: "Remover",
          cancelTitle: "Cancelar",
          okVariant: "danger",
          centered: true,
        }
      );
      if (!confirmed) return;
      const index = this.dataInspection.diagnosticos.findIndex(
        (item) => item.id === id
      );
      if (index < 0) return;
      this.dataInspection.diagnosticos.splice(index, 1);
      this.dataInspection.fotos = (this.dataInspection.fotos || []).filter(
        (photo) => (photo?._diagnosisId || photo?.diagnosisId) !== id
      );
      if (this.activeDiagnosisId === id) {
        const next =
          this.dataInspection.diagnosticos[
            Math.min(index, this.dataInspection.diagnosticos.length - 1)
          ];
        this.selectDiagnosis(next.id);
      }
    },

    serializeDiagnosis(source) {
      const diagnosis = { ...source };
      delete diagnosis.talhaoMode;
      delete diagnosis.cultureMode;
      return diagnosis;
    },

    hydrateMipData() {
      const defaultSampling = {
        method: "",
        samplePoints: null,
        assessedArea: null,
        distribution: "",
        notes: "",
      };
      this.dataInspection.diagnosticos.forEach((diagnosis) => {
        diagnosis.mipSampling = {
          ...defaultSampling,
          ...(diagnosis.mipSampling || {}),
        };

        ["praga", "doenca", "daninha"].forEach((type) => {
          const collections = {
            praga: "alvosPragas",
            doenca: "alvosDoencas",
            daninha: "alvosDaninhas",
          };
          diagnosis[collections[type]].forEach((target) => {
            const catalogTarget = this.mipTargetCatalog[type].find(
              (item) =>
                item.id === target.catalogId ||
                item.nome.toLowerCase() === String(target.nome).toLowerCase()
            );
            if (catalogTarget && !target.catalogId) {
              this.$set(target, "catalogId", catalogTarget.id);
            }
            if (!target.nomeCientifico) {
              this.$set(
                target,
                "nomeCientifico",
                catalogTarget?.nomeCientifico || ""
              );
            }
            if (target.observacao === undefined) {
              this.$set(target, "observacao", "");
            }
            if (type === "daninha" && target.cobertura === undefined) {
              this.$set(target, "cobertura", 0);
            }
          });
        });
      });
    },

    toggleReferenceMode(field) {
      const diagnosisModeField = {
        talhao: "talhaoMode",
        culture: "cultureMode",
      }[field];
      const currentMode = diagnosisModeField
        ? this.activeDiagnosis[diagnosisModeField]
        : this.referenceModes[field];
      const registered = currentMode === "registered";
      const nextMode = registered ? "manual" : "registered";
      if (diagnosisModeField) {
        this.activeDiagnosis[diagnosisModeField] = nextMode;
      } else {
        this.referenceModes[field] = nextMode;
      }

      const fields = {
        customer: ["idCliente", "customerName"],
        property: ["idTalhao", "propertyName"],
      };
      const fieldsToClear = fields[field];
      if (fieldsToClear) {
        fieldsToClear.forEach((fieldName) => {
          this.dataInspection[fieldName] = "";
        });
      } else if (field === "talhao") {
        this.activeDiagnosis.nomeTalhao = "";
        this.activeDiagnosis.talhaoId = "";
      } else if (field === "culture") {
        this.activeDiagnosis.culturaSelecionada = "";
        this.activeDiagnosis.cultureName = "";
      }

      if (field === "customer") {
        this.dataProperties = [];
        this.dataTalhoes = [];
        this.dataInspection.idTalhao = "";
        this.dataInspection.propertyName = "";
        this.dataInspection.diagnosticos.forEach((diagnosis) => {
          diagnosis.nomeTalhao = "";
          diagnosis.talhaoId = "";
        });
        this.referenceModes.property = nextMode;
      }

      if (field === "property") {
        this.dataTalhoes = [];
        this.dataInspection.diagnosticos.forEach((diagnosis) => {
          diagnosis.nomeTalhao = "";
          diagnosis.talhaoId = "";
        });
      }
    },

    async loadTechnicalManagers() {
      try {
        this.dataTechnicalManagers =
          (await serviceResponsible.getResponsibleForSelectBox(
            this.dataInspection.idResponsavelTecnico || undefined
          )) || [];
        this.reconcileResponsibleSelection();
      } catch (error) {
        this.dataTechnicalManagers = [];
        this.$toast.open({
          message: "Não foi possível carregar os responsáveis técnicos",
          type: "warning",
        });
      }
    },

    async loadEmployees() {
      try {
        const { data } = await serviceEmployee.getEmployeesForSelectBox();
        this.dataEmployees = data || [];
      } catch (error) {
        this.dataEmployees = [];
        this.$toast.open({
          message: "Não foi possível carregar os funcionários",
          type: "warning",
        });
      }
    },

    async loadVehicles() {
      try {
        const vehicles = (await serviceVehicle.fillCombobox()) || [];
        this.dataVehicles = [{ id: "", placa: "Nenhum veículo" }, ...vehicles];
      } catch (error) {
        this.dataVehicles = [{ id: "", placa: "Nenhum veículo" }];
        this.$toast.open({
          message: "Não foi possível carregar os veículos",
          type: "warning",
        });
      }
    },

    reconcileResponsibleSelection() {
      if (this.dataInspection.idResponsavelTecnico) return;
      const savedName = String(this.dataInspection.professionalName || "")
        .trim()
        .toLocaleLowerCase();
      if (!savedName) return;
      const responsible = this.dataTechnicalManagers.find(
        (item) =>
          String(item.nome || "")
            .trim()
            .toLocaleLowerCase() === savedName
      );
      if (responsible) {
        this.dataInspection.idResponsavelTecnico = responsible.id;
      }
    },
    async loadProperties(customerId, preserveSelection = false) {
      if (!customerId) {
        this.dataProperties = [];
        return;
      }
      try {
        this.dataProperties =
          (await serviceCustomerProperty.fillComobobox(customerId)) || [];
        if (!preserveSelection) {
          this.dataInspection.idTalhao = "";
          this.dataInspection.propertyName = "";
          this.dataInspection.diagnosticos.forEach((diagnosis) => {
            diagnosis.nomeTalhao = "";
            diagnosis.talhaoId = "";
          });
          this.dataTalhoes = [];
          if (this.$refs.refProperty) this.$refs.refProperty.valueItems = "";
        }
      } catch (error) {
        this.dataProperties = [];
        this.$toast.open({
          message: "Não foi possível carregar as propriedades do cliente",
          type: "warning",
        });
      }
    },
    async loadTalhoes(propertyId, preserveSelection = false) {
      if (!propertyId) {
        this.dataTalhoes = [];
        if (preserveSelection && this.activeDiagnosis.nomeTalhao) {
          this.activeDiagnosis.talhaoMode = "manual";
        }
        return;
      }
      try {
        this.dataTalhoes =
          (await serviceTalhao.findByPropertyId(propertyId)) || [];
        if (!preserveSelection) {
          this.activeDiagnosis.nomeTalhao = "";
          this.activeDiagnosis.talhaoId = "";
          if (this.$refs.refTalhao) this.$refs.refTalhao.valueItems = "";
          return;
        }

        this.normalizeTalhaoSelection();
      } catch (error) {
        this.dataTalhoes = [];
        if (preserveSelection && this.activeDiagnosis.nomeTalhao) {
          this.activeDiagnosis.talhaoMode = "manual";
        }
        this.$toast.open({
          message: "Não foi possível carregar os talhões da propriedade",
          type: "warning",
        });
      }
    },

    normalizeTalhaoSelection() {
      const savedValue = String(
        this.activeDiagnosis.talhaoId || this.activeDiagnosis.nomeTalhao || ""
      );
      if (!savedValue) {
        this.activeDiagnosis.talhaoMode = "registered";
        return;
      }

      const talhaoById = this.dataTalhoes.find(
        (talhao) => String(talhao.id) === savedValue
      );
      if (talhaoById) {
        this.activeDiagnosis.talhaoId = talhaoById.id || "";
        this.activeDiagnosis.nomeTalhao = talhaoById.nome || "";
      }

      const selectedName = String(this.activeDiagnosis.nomeTalhao || "");
      const isRegistered = this.dataTalhoes.some(
        (talhao) => String(talhao.nome || "") === selectedName
      );
      this.activeDiagnosis.talhaoMode = isRegistered ? "registered" : "manual";
    },

    handleResponsibleSelection(responsible) {
      this.dataInspection.professionalName = responsible?.nome || "";
    },

    async handlePropertySelection(property) {
      this.dataInspection.idTalhao = property?.id || "";
      this.dataInspection.propertyName = property?.nome || "";
      this.dataInspection.diagnosticos.forEach((diagnosis) => {
        diagnosis.talhaoId = "";
        diagnosis.nomeTalhao = "";
      });
      await this.loadTalhoes(this.dataInspection.idTalhao, true);
    },

    handleTalhaoSelection(talhao) {
      this.activeDiagnosis.talhaoId = talhao?.id || "";
      this.activeDiagnosis.nomeTalhao = talhao?.nome || "";
    },

    handleCultureSelection(culture) {
      this.activeDiagnosis.cultureName = culture?.nome || "";
      this.activeDiagnosis.culturaSelecionada = culture?.id || "";
    },

    async fillCulturesWithPaginate(id, query, filterType, page = 1) {
      try {
        if (id) {
          const culture = await serviceCompendio.findCultureById(id);
          return culture ? [this.normalizeCulture(culture)] : [];
        }
        const result = await serviceCompendio.findCultures(query, page);
        const cultures = Array.isArray(result) ? result : result?.data || [];
        return cultures.map(this.normalizeCulture);
      } catch (error) {
        this.$toast.open({
          message: "Não foi possível carregar as culturas do Compêndio",
          type: "warning",
        });
        return [];
      }
    },

    normalizeCulture(culture) {
      const scientificName = culture.nomeCientifico || culture.nomecientifico;
      return {
        ...culture,
        id: String(culture.id),
        nome: scientificName
          ? `${culture.nome} (${scientificName})`
          : culture.nome,
      };
    },

    capturarLocalizacao() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          this.dataInspection.coordenadas.lat = position.coords.latitude;
          this.dataInspection.coordenadas.lng = position.coords.longitude;
          this.$toast.open({
            message: "Coordenadas capturadas com sucesso!",
            type: "success",
          });
        });
      } else {
        this.$toast.open({
          message: "Geolocalização não suportada pelo navegador.",
          type: "warning",
        });
      }
    },
    handleFileUpload(event) {
      const files = event.target.files;
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        file._isRawFile = true;
        file._diagnosisId = this.activeDiagnosis.id;
        file._latitude = null;
        file._longitude = null;
        this.dataInspection.fotos.push(file);
      }
      event.target.value = "";
    },
    handleAudioCompleted(audio) {
      this.dataInspection.fotos.push({
        ...audio,
        type: "audio",
        diagnosisId: this.activeDiagnosis.id,
      });
      this.$toast.open({
        message: "Áudio anexado com sucesso",
        type: "success",
      });
    },
    /* TRANSCRIÇÃO EM STANDBY
    updateAudioTranscription({ recording, value }) {
      this.$set(recording, "transcription", value);
    },
    applyAudioTranscriptionResult({ recording, result }) {
      this.$set(recording, "transcriptionStatus", result.status);
      if (result.jobId) {
        this.$set(recording, "transcriptionJobId", result.jobId);
      }
      if (result.transcriptionText) {
        this.$set(recording, "transcription", result.transcriptionText);
      }
    },
    */
    removeAudio(recording) {
      this.dataInspection.fotos = (this.dataInspection.fotos || []).filter(
        (attachment) => attachment !== recording
      );
    },
    hasPhotoCoords(foto) {
      const lat = Number(foto._latitude ?? foto.latitude);
      const lng = Number(foto._longitude ?? foto.longitude);
      return Number.isFinite(lat) && Number.isFinite(lng);
    },
    openPhotoLocation(foto) {
      if (this.photoLocationTarget === foto) {
        this.closePhotoLocation();
        return;
      }
      this.photoLocationTarget = foto;
      this.photoLocationLat = foto._latitude ?? foto.latitude ?? null;
      this.photoLocationLng = foto._longitude ?? foto.longitude ?? null;
    },
    onPhotoLocationSelected(coords) {
      if (!this.photoLocationTarget) return;
      const target = this.photoLocationTarget;
      if (target._isRawFile || target instanceof File) {
        this.$set(target, "_latitude", coords.latitude);
        this.$set(target, "_longitude", coords.longitude);
      } else {
        this.$set(target, "latitude", coords.latitude);
        this.$set(target, "longitude", coords.longitude);
      }
      this.photoLocationLat = coords.latitude;
      this.photoLocationLng = coords.longitude;
    },
    closePhotoLocation() {
      this.photoLocationTarget = null;
      this.photoLocationLat = null;
      this.photoLocationLng = null;
    },
    isRawFile(file) {
      return Boolean(
        file &&
          (file._isRawFile ||
            (typeof File !== "undefined" && file instanceof File))
      );
    },
    getObjectURL(file) {
      try {
        return URL.createObjectURL(file);
      } catch {
        return "";
      }
    },

    extractProblemId(catalogId) {
      if (!catalogId) return null;
      const str = String(catalogId);
      if (str.startsWith("compendio-")) {
        return str.replace("compendio-", "");
      }
      const parts = str.trim().split(/\s+/);
      const last = parts[parts.length - 1];
      if (/^\d+$/.test(last)) return last;
      return null;
    },

    normalizePesticideRegister(value) {
      return String(value || "")
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");
    },

    stockMatchesFor(agrotoxico) {
      const pesticideId = String(agrotoxico?.id || "");
      const register = this.normalizePesticideRegister(agrotoxico?.registro);

      return this.pesticidesInStock.filter((product) => {
        const sameId =
          pesticideId &&
          product.idAgrotoxico &&
          String(product.idAgrotoxico) === pesticideId;
        const sameRegister =
          register &&
          this.normalizePesticideRegister(product.registroAgrotox) === register;
        return sameId || sameRegister;
      });
    },

    filterSuggestionsByStock(list) {
      return this.decorateSuggestionsWithStock(list).filter(
        (agrotoxico) => agrotoxico.emEstoque
      );
    },

    decorateSuggestionsWithStock(list) {
      const suggestions = [];
      const seen = new Set();

      list.forEach((agrotoxico) => {
        const products = this.stockMatchesFor(agrotoxico);
        const key = String(
          agrotoxico.id || this.normalizePesticideRegister(agrotoxico.registro)
        );
        if (seen.has(key)) return;
        seen.add(key);

        suggestions.push({
          ...agrotoxico,
          emEstoque: products.length > 0,
          stockProducts: products,
        });
      });

      return suggestions;
    },

    normalizeProblemName(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLocaleLowerCase();
    },

    async loadCompendiumProblemsForSearch(type) {
      if (this.compendiumProblemCache[type]) {
        return this.compendiumProblemCache[type];
      }

      const categoryIds = { doenca: 1, praga: 2, daninha: 3 };
      const result = await serviceCompendio.findAllProblems(categoryIds[type]);
      const problems = Array.isArray(result) ? result : result?.data || [];
      this.$set(this.compendiumProblemCache, type, problems);
      return problems;
    },

    async resolveCompendiumTargets(targets) {
      const missingTypes = [
        ...new Set(
          targets
            .filter((target) => !this.extractProblemId(target.catalogId))
            .map((target) => target.problemType)
        ),
      ];
      const catalogs = {};

      await Promise.allSettled(
        missingTypes.map(async (type) => {
          catalogs[type] = await this.loadCompendiumProblemsForSearch(type);
        })
      );

      return targets.map((target) => {
        const existingId = this.extractProblemId(target.catalogId);
        if (existingId) return { ...target, problemaId: existingId };

        const targetName = this.normalizeProblemName(target.nome);
        const scientificName = this.normalizeProblemName(target.nomeCientifico);
        const match = (catalogs[target.problemType] || []).find((problem) => {
          const common = this.normalizeProblemName(
            problem.nomeComum || problem.nome
          );
          const scientific = this.normalizeProblemName(problem.nomeCientifico);
          return (
            (targetName && targetName === common) ||
            (scientificName && scientificName === scientific)
          );
        });

        if (!match) return { ...target, problemaId: null };
        const catalogId = `compendio-${match.id}`;
        return { ...target, catalogId, problemaId: String(match.id) };
      });
    },

    async consultarAssistenteIA() {
      await this.searchPesticideSuggestions(false);
    },

    async pesquisarNoCompendio() {
      await this.searchPesticideSuggestions(true);
    },

    async retryPesticideSearch() {
      await this.searchPesticideSuggestions(
        this.suggestionSource === "compendium"
      );
    },

    resetPesticideSuggestions() {
      this.agrotoxicSearchRequestId += 1;
      this.loadingAgrotoxics = false;
      this.hasSearchedAgrotoxics = false;
      this.failedAgrotoxicTargets = 0;
      this.unresolvedAgrotoxicTargets = 0;
      this.suggestionSource = "stock";
      this.agrotoxicSuggestions = {};
      this.agrotoxicDetails = {};
      this.agrotoxicDates = {};
      this.agrotoxicStockProductIds = {};
      this.agrotoxicApplicationMethods = {};
      this.agrotoxicApplicationMethodIds = {};
      this.agrotoxicProblemIds = {};
      this.loadingAgrotoxicDetails = {};
      this.searchAgrotoxics = "";
    },

    async searchPesticideSuggestions(includeOutOfStock = false) {
      const diagnosesWithTargets = this.dataInspection.diagnosticos.filter(
        (diagnosis) => this.diagnosisTargets(diagnosis).length
      );
      const diagnosesWithoutCulture = diagnosesWithTargets.filter(
        (diagnosis) => !diagnosis.culturaSelecionada
      );
      const allTargets = diagnosesWithTargets.flatMap((diagnosis) =>
        diagnosis.culturaSelecionada
          ? this.diagnosisTargets(diagnosis).map((target) => ({
              ...target,
              diagnosisId: diagnosis.id,
              cultureId: diagnosis.culturaSelecionada,
              cultureName: diagnosis.cultureName,
            }))
          : []
      );

      if (!allTargets.length) {
        this.$toast.open({
          message: diagnosesWithoutCulture.length
            ? "Selecione a cultura dos diagnósticos antes da consulta."
            : "Adicione pelo menos um diagnóstico fitossanitário antes da consulta.",
          type: "info",
        });
        return;
      }

      const requestId = ++this.agrotoxicSearchRequestId;
      this.loadingAgrotoxics = true;
      this.hasSearchedAgrotoxics = true;
      this.suggestionSource = includeOutOfStock ? "compendium" : "stock";
      this.agrotoxicSuggestions = {};
      this.agrotoxicDetails = {};
      this.agrotoxicDates = {};
      this.agrotoxicStockProductIds = {};
      this.agrotoxicApplicationMethods = {};
      this.agrotoxicApplicationMethodIds = {};
      this.agrotoxicProblemIds = {};
      this.loadingAgrotoxicDetails = {};
      this.failedAgrotoxicTargets = 0;
      this.unresolvedAgrotoxicTargets = 0;

      this.$toast.open({
        message: includeOutOfStock
          ? "Buscando defensivos no Compêndio Agronômico..."
          : "Verificando os defensivos disponíveis em estoque...",
        type: "info",
      });

      try {
        const resolvedTargets = await this.resolveCompendiumTargets(allTargets);
        const compendiumTargets = resolvedTargets.filter(
          (target) => target.problemaId
        );
        const unresolvedCount =
          resolvedTargets.length - compendiumTargets.length;
        this.unresolvedAgrotoxicTargets = unresolvedCount;

        if (!compendiumTargets.length) {
          this.$toast.open({
            message: "Nenhum diagnóstico pôde ser relacionado ao Compêndio.",
            type: "info",
          });
          return;
        }

        const pendingSearches = {};
        const results = await Promise.allSettled(
          compendiumTargets.map(async (target) => {
            const problemaId = target.problemaId;
            const cacheKey = `${target.cultureId}-${problemaId}`;
            let data = this.agrotoxicSearchCache[cacheKey];
            if (!data) {
              if (!pendingSearches[cacheKey]) {
                pendingSearches[cacheKey] =
                  serviceCompendio.findAgrotoxicsByCultureAndProblem(
                    target.cultureId,
                    problemaId
                  );
              }
              data = await pendingSearches[cacheKey];
              this.$set(
                this.agrotoxicSearchCache,
                cacheKey,
                Array.isArray(data) ? data : []
              );
            }
            return {
              diagnosisId: target.diagnosisId,
              targetId: target.id,
              problemaId,
              data,
            };
          })
        );

        if (requestId !== this.agrotoxicSearchRequestId) return;

        const rejectedCount = results.filter(
          (result) => result.status === "rejected"
        ).length;
        this.failedAgrotoxicTargets = rejectedCount;
        let foundCount = 0;
        results.forEach((result) => {
          if (result.status === "fulfilled") {
            const { targetId, problemaId, data } = result.value;
            const compendiumList = Array.isArray(data) ? data : [];
            const list = includeOutOfStock
              ? this.decorateSuggestionsWithStock(compendiumList)
              : this.filterSuggestionsByStock(compendiumList);
            if (list.length) {
              this.$set(this.agrotoxicSuggestions, targetId, list);
              foundCount += list.length;
              list.forEach((agro) => {
                const detailKey = `${targetId}-${agro.id}`;
                this.$set(this.agrotoxicProblemIds, detailKey, problemaId);
              });
            }
          }
        });

        const pesticideIds = [
          ...new Set(
            Object.values(this.agrotoxicSuggestions)
              .flat()
              .map((item) => item.id)
          ),
        ];
        await Promise.all(
          pesticideIds.map((id) => this.loadApplicationMethods(id))
        );

        if (foundCount > 0) {
          this.$toast.open({
            message: includeOutOfStock
              ? `${foundCount} defensivo(s) encontrado(s) no Compêndio.`
              : `${foundCount} defensivo(s) compatível(is) encontrado(s) em estoque.`,
            type: "success",
          });
        } else if (this.failedAgrotoxicTargets) {
          this.$toast.open({
            message: `Não foi possível consultar ${this.failedAgrotoxicTargets} alvo(s).`,
            type: "warning",
          });
        } else {
          this.$toast.open({
            message: includeOutOfStock
              ? "Nenhum defensivo encontrado no Compêndio para os alvos diagnosticados."
              : "Nenhum defensivo compatível está disponível em estoque.",
            type: "info",
          });
        }
      } catch (error) {
        this.$toast.open({
          message: "Erro ao consultar o Compêndio. Tente novamente.",
          type: "error",
        });
      } finally {
        if (requestId === this.agrotoxicSearchRequestId) {
          this.loadingAgrotoxics = false;
        }
      }
    },

    async loadAgrotoxicDetails(agrotoxico, target, cultureId) {
      const detailKey = `${target.id}-${agrotoxico.id}`;
      if (this.agrotoxicDetails[detailKey]) {
        return this.agrotoxicDetails[detailKey];
      }

      const problemaId = this.agrotoxicProblemIds[detailKey];
      if (!problemaId) return {};

      this.$set(this.loadingAgrotoxicDetails, detailKey, true);
      try {
        const details = await serviceCompendio.findAgrotoxicDetails(
          cultureId,
          problemaId,
          agrotoxico.id
        );
        this.$set(this.agrotoxicDetails, detailKey, details || {});
        return details || {};
      } catch (error) {
        this.$toast.open({
          message:
            "Não foi possível carregar dose e carência; a indicação será adicionada sem esses dados.",
          type: "warning",
        });
        return {};
      } finally {
        this.$delete(this.loadingAgrotoxicDetails, detailKey);
      }
    },

    async addSuggestionToRecommendation(agrotoxico, target, diagnosis) {
      const cultura = diagnosis.cultureName || "a cultura";
      const problema = target.nome;
      const dateKey = `${target.id}-${agrotoxico.id}`;
      const dataAplicacao = this.agrotoxicDates[dateKey];

      if (!dataAplicacao) {
        this.$toast.open({
          message: "Informe a data de aplicação antes de adicionar",
          type: "warning",
        });
        return;
      }

      const stockProduct = this.selectedStockProduct(target.id, agrotoxico);
      if (agrotoxico.emEstoque && !stockProduct) {
        this.$toast.open({
          message: "Selecione um produto disponível no estoque para adicionar",
          type: "warning",
        });
        return;
      }

      const entry = `${agrotoxico.nomeComum} (${agrotoxico.registro})`;
      const alreadyAdded = diagnosis.recommendedProducts.some(
        (item) =>
          String(item.stockProductId || item.pesticideId) ===
            String(stockProduct?.id || agrotoxico.id) &&
          String(item.targetId) === String(target.id)
      );
      if (alreadyAdded) {
        this.$toast.open({
          message: "Este produto já foi vinculado a este diagnóstico",
          type: "info",
        });
        return;
      }

      const problemId = this.agrotoxicProblemIds[dateKey];
      let details;
      let indications;
      try {
        [details, indications] = await Promise.all([
          this.loadAgrotoxicDetails(
            agrotoxico,
            target,
            diagnosis.culturaSelecionada
          ),
          serviceCompendio.findDiagnosysIdCulture(
            agrotoxico.id,
            diagnosis.culturaSelecionada
          ),
        ]);
      } catch (error) {
        this.$toast.open({
          message:
            "Não foi possível carregar os dados técnicos completos do produto.",
          type: "error",
        });
        return;
      }
      const indication = (Array.isArray(indications) ? indications : []).find(
        (item) => String(item.idProblema) === String(problemId)
      );
      const application = this.selectedApplicationMethod(
        target.id,
        agrotoxico.id
      );
      const packageData = stockProduct
        ? await this.resolveStockPackage(stockProduct, agrotoxico.id)
        : null;

      const missingData = [];
      if (stockProduct && !stockProduct.id)
        missingData.push("produto do estoque");
      if (stockProduct && !packageData?.id)
        missingData.push("identificador da embalagem");
      if (stockProduct && !packageData?.description)
        missingData.push("descrição da embalagem");
      if (!indication?.idIndicacao) missingData.push("indicação para o alvo");
      if (!application?.id) missingData.push("modalidade de aplicação");
      if (missingData.length) {
        this.$toast.open({
          message: `Não foi possível resolver: ${missingData.join(
            ", "
          )}. Verifique o vínculo do produto com o Compêndio.`,
          type: "warning",
        });
        return;
      }
      const dose =
        details.doses && details.doses.length
          ? `${details.doses[0].doseMinima} a ${details.doses[0].doseMaxima} ${details.doses[0].unidadeDose}`
          : null;
      const carencia =
        details.intervalos &&
        details.intervalos.length &&
        details.intervalos[0].seguranca
          ? details.intervalos[0].seguranca
          : null;

      const dataFormatada = new Date(
        dataAplicacao + "T00:00:00"
      ).toLocaleDateString("pt-BR");

      let texto =
        `Para o controle de ${problema} em ${cultura}, ` +
        `recomenda-se aplicação de ${entry} ` +
        `na data de ${dataFormatada}.`;

      if (dose) {
        texto += ` Respeitar dose indicada na bula (${dose}).`;
      }
      if (carencia) {
        texto += ` Intervalo de carência de ${carencia} dias.`;
      }

      const doseData = details.doses?.[0] || indication || {};
      const intervalData = details.intervalos?.[0] || {};
      diagnosis.recommendedProducts.push(
        createInspectionRecommendation({
          diagnosisId: diagnosis.id,
          cultureId: diagnosis.culturaSelecionada,
          cultureName: diagnosis.cultureName,
          targetId: target.id,
          targetCatalogId: target.catalogId || null,
          problemId,
          targetType: target.targetType || this.targetType(target, diagnosis),
          targetName: target.nome,
          targetScientificName: target.nomeCientifico || "",
          pesticideId: agrotoxico.id,
          pesticideName: agrotoxico.nomeComum,
          pesticideRegister: agrotoxico.registro,
          stockProductId: stockProduct?.id || null,
          stockProductName: stockProduct?.nome || "",
          packageId: packageData?.id || null,
          packageDescription: packageData?.description || "",
          batch: stockProduct?.lote || "",
          stockAtRecommendation: stockProduct?.estoque ?? null,
          unitPrice: stockProduct?.valorVenda || 0,
          taxReference: stockProduct?.refFiscal || null,
          ncm: stockProduct?.ncm || null,
          indicationId: indication.idIndicacao,
          doseMinimum: doseData.doseMinima,
          doseMaximum: doseData.doseMaxima,
          doseUnit: doseData.unidadeDose,
          minimumSprayVolume:
            doseData.minVolumeCalda ?? doseData.doseMinimaVolumeCalda,
          maximumSprayVolume:
            doseData.maxVolumeCalda ?? doseData.doseMaximaVolumeCalda,
          sprayVolumeUnit: indication.unidadeVolumeCalda,
          safetyInterval: intervalData.seguranca,
          reentryInterval: intervalData.reentrada,
          applicationInterval: intervalData.intervaloAplicacoes,
          applicationDate: dataAplicacao,
          applicationMethodId: application.id,
          applicationMethodName: application.name,
          applicationMethodDescription: application.description,
          source: stockProduct ? "stock" : "compendium",
        })
      );

      const latestRecommendation = diagnosis.recomendacaoManejo || "";
      if (
        !latestRecommendation.includes(entry) ||
        !latestRecommendation.includes(problema)
      ) {
        const separator = latestRecommendation.trim() ? "\n" : "";
        diagnosis.recomendacaoManejo = latestRecommendation + separator + texto;
      }
      this.$toast.open({
        message: stockProduct
          ? "Produto do estoque vinculado à cultura e ao diagnóstico"
          : "Produto do Compêndio vinculado à inspeção sem disponibilidade em estoque",
        type: "success",
      });
    },

    selectedStockProductId(targetId, agrotoxico) {
      const key = `${targetId}-${agrotoxico.id}`;
      return (
        this.agrotoxicStockProductIds[key] ||
        agrotoxico.stockProducts?.[0]?.id ||
        ""
      );
    },

    selectedStockProduct(targetId, agrotoxico) {
      const selectedId = this.selectedStockProductId(targetId, agrotoxico);
      return (agrotoxico.stockProducts || []).find(
        (product) => String(product.id) === String(selectedId)
      );
    },

    setSelectedStockProduct(targetId, agrotoxicoId, productId) {
      this.$set(
        this.agrotoxicStockProductIds,
        `${targetId}-${agrotoxicoId}`,
        productId
      );
    },

    normalizePackageDescription(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .toLocaleLowerCase();
    },

    async resolveStockPackage(stockProduct, agrotoxicoId) {
      const currentId = Number(stockProduct?.idEmbalagem);
      const currentDescription = String(stockProduct?.embalagem || "").trim();
      if (Number.isFinite(currentId) && currentId > 0 && currentDescription) {
        return { id: currentId, description: currentDescription };
      }

      try {
        const packages = await serviceCompendio.findAllEmbalagensByIdAgrotoxico(
          agrotoxicoId
        );
        const normalizedCurrent =
          this.normalizePackageDescription(currentDescription);
        const packageById = (packages || []).find(
          (item) => Number(item.id) === currentId
        );
        const exactPackage = (packages || []).find(
          (item) =>
            this.normalizePackageDescription(item.descricao) ===
            normalizedCurrent
        );
        const compatiblePackages = normalizedCurrent
          ? (packages || []).filter((item) => {
              const description = this.normalizePackageDescription(
                item.descricao
              );
              return (
                description.includes(normalizedCurrent) ||
                normalizedCurrent.includes(description)
              );
            })
          : [];
        const matchedPackage =
          packageById ||
          exactPackage ||
          (compatiblePackages.length === 1 ? compatiblePackages[0] : null);
        if (!matchedPackage) {
          return {
            id: Number.isFinite(currentId) && currentId > 0 ? currentId : null,
            description: currentDescription,
          };
        }
        return {
          id: matchedPackage.id,
          description: matchedPackage.descricao,
        };
      } catch (error) {
        return {
          id: Number.isFinite(currentId) && currentId > 0 ? currentId : null,
          description: currentDescription,
        };
      }
    },

    async loadApplicationMethods(agrotoxicoId) {
      if (this.agrotoxicApplicationMethods[agrotoxicoId]) {
        return this.agrotoxicApplicationMethods[agrotoxicoId];
      }
      try {
        const data = await serviceCompendio.findApplicationMethods(
          agrotoxicoId
        );
        const methods = (data?.aplicacoes || [])
          .map((application) => ({
            id: application.tiposAplicacoes?.id,
            name: application.tiposAplicacoes?.nome || "",
            description: application.descricao || "",
          }))
          .filter((method) => method.id && method.name);
        this.$set(this.agrotoxicApplicationMethods, agrotoxicoId, methods);
        return methods;
      } catch (error) {
        this.$set(this.agrotoxicApplicationMethods, agrotoxicoId, []);
        return [];
      }
    },

    applicationMethods(agrotoxicoId) {
      return this.agrotoxicApplicationMethods[agrotoxicoId] || [];
    },

    selectedApplicationMethodId(targetId, agrotoxicoId) {
      const methods = this.applicationMethods(agrotoxicoId);
      return (
        this.agrotoxicApplicationMethodIds[`${targetId}-${agrotoxicoId}`] ||
        (methods.length === 1 ? methods[0].id : "")
      );
    },

    selectedApplicationMethod(targetId, agrotoxicoId) {
      const selectedId = this.selectedApplicationMethodId(
        targetId,
        agrotoxicoId
      );
      return this.applicationMethods(agrotoxicoId).find(
        (method) => String(method.id) === String(selectedId)
      );
    },

    setSelectedApplicationMethod(targetId, agrotoxicoId, methodId) {
      this.$set(
        this.agrotoxicApplicationMethodIds,
        `${targetId}-${agrotoxicoId}`,
        methodId
      );
    },

    stockProductLabel(product) {
      const parts = [product.nome, product.embalagem];
      if (product.lote) parts.push(`lote ${product.lote}`);
      parts.push(`estoque ${Number(product.estoque || 0)}`);
      return parts.filter(Boolean).join(" · ");
    },

    diagnosisTargets(diagnosis) {
      return [
        ...(diagnosis.alvosPragas || []).map((target) => ({
          ...target,
          targetType: "praga",
        })),
        ...(diagnosis.alvosDoencas || []).map((target) => ({
          ...target,
          targetType: "doenca",
        })),
        ...(diagnosis.alvosDaninhas || []).map((target) => ({
          ...target,
          targetType: "daninha",
        })),
      ];
    },

    targetIcon(type) {
      return { praga: "🐜", doenca: "🦠", daninha: "🌱" }[type] || "🌿";
    },

    targetType(target, diagnosis) {
      if (diagnosis.alvosPragas.some((item) => item.id === target.id))
        return "praga";
      if (diagnosis.alvosDoencas.some((item) => item.id === target.id))
        return "doenca";
      return "daninha";
    },

    removeRecommendedProduct(id) {
      const index = this.activeDiagnosis.recommendedProducts.findIndex(
        (item) => item.id === id
      );
      if (index >= 0) this.activeDiagnosis.recommendedProducts.splice(index, 1);
    },

    formatApplicationDate(value) {
      return value ? moment(value).format("DD/MM/YYYY") : "não informada";
    },

    setAgrotoxicDate(targetId, agrotoxicoId, value) {
      this.$set(this.agrotoxicDates, `${targetId}-${agrotoxicoId}`, value);
    },

    targetSuggestions(targetId) {
      const list = this.agrotoxicSuggestions[targetId] || [];
      if (!this.searchAgrotoxics.trim()) return list;
      const q = this.searchAgrotoxics.toLowerCase();
      return list.filter(
        (a) =>
          a.nomeComum.toLowerCase().includes(q) ||
          (a.registro && a.registro.toLowerCase().includes(q))
      );
    },

    allFilteredCount() {
      const all = this.dataInspection.diagnosticos.flatMap((diagnosis) =>
        this.diagnosisTargets(diagnosis)
      );
      return all.reduce(
        (sum, t) => sum + this.targetSuggestions(t.id).length,
        0
      );
    },

    async saveInspection(concluded = false) {
      if (this.isSavingInspection) return null;
      this.isSavingInspection = true;
      try {
        return await this.persistInspection(concluded);
      } finally {
        this.isSavingInspection = false;
      }
    },
    async persistInspection(concluded = false) {
      const hasCustomer =
        this.dataInspection.idCliente || this.dataInspection.customerName;
      if (!hasCustomer || !this.dataInspection.idResponsavelTecnico) {
        this.$toast.open({
          message: "Informe o produtor e o responsável técnico",
          type: "warning",
        });
        return null;
      }
      const invalidDiagnosis = this.dataInspection.diagnosticos.find(
        (diagnosis) =>
          !String(diagnosis.nomeTalhao || "").trim() ||
          !String(diagnosis.cultureName || "").trim()
      );
      if (invalidDiagnosis) {
        this.selectDiagnosis(invalidDiagnosis.id);
        this.$toast.open({
          message: "Informe o talhão e a cultura de todos os diagnósticos",
          type: "warning",
        });
        return null;
      }
      const diagnosisWithInvalidProduct = this.dataInspection.diagnosticos.find(
        (diagnosis) =>
          diagnosis.recommendedProducts.some(
            (product) =>
              String(product.diagnosisId) !== String(diagnosis.id) ||
              String(product.cultureId) !== String(diagnosis.culturaSelecionada)
          )
      );
      if (diagnosisWithInvalidProduct) {
        this.selectDiagnosis(diagnosisWithInvalidProduct.id);
        this.$toast.open({
          message:
            "A cultura deste diagnóstico mudou. Remova e adicione novamente os produtos vinculados.",
          type: "warning",
        });
        return null;
      }
      const invalidAction = this.dataInspection.pendingActions.some(
        (item) => !item.description || !item.responsible || !item.deadline
      );
      if (invalidAction) {
        this.$toast.open({
          message: "Preencha descrição, responsável e prazo das providências",
          type: "warning",
        });
        return null;
      }
      const newFiles = [];
      const existingAttachments = [];
      this.dataInspection.fotos.forEach((file) => {
        if (this.isRawFile(file)) {
          newFiles.push(file);
        } else {
          existingAttachments.push({
            id: file.id,
            name: file.name || file,
            type: file.type || null,
            key: file.key || null,
            url: file.url || null,
            date: file.date || new Date().toISOString(),
            diagnosisId: file.diagnosisId || file.diagnosticoId || null,
            latitude: file.latitude || null,
            longitude: file.longitude || null,
            mimeType: file.mimeType || null,
            size: file.size ?? null,
            durationSeconds: file.durationSeconds ?? null,
            transcription: file.transcription || "",
            transcriptionJobId: file.transcriptionJobId || null,
            transcriptionStatus: file.transcriptionStatus || null,
          });
        }
      });
      const attachments = existingAttachments.concat(
        newFiles.map((file) => ({
          name: file.name,
          type: "image",
          date: new Date().toISOString(),
          diagnosisId: file._diagnosisId || null,
          latitude: file._latitude || null,
          longitude: file._longitude || null,
        }))
      );
      const dataInspection = { ...this.dataInspection };
      delete dataInspection.fotos;
      delete dataInspection.placaVeiculo;
      const firstDiagnosis = this.dataInspection.diagnosticos[0];
      const talhoes = [
        ...new Set(
          this.dataInspection.diagnosticos.map((item) => item.nomeTalhao)
        ),
      ].filter(Boolean);
      const cultures = [
        ...new Set(
          this.dataInspection.diagnosticos.map((item) => item.cultureName)
        ),
      ].filter(Boolean);
      const inspection = {
        ...dataInspection,
        diagnosticos: this.dataInspection.diagnosticos.map(
          this.serializeDiagnosis
        ),
        id: this.dataInspection.id || undefined,
        customerId: this.dataInspection.idCliente || null,
        customerName: this.dataInspection.customerName,
        idFuncionario: this.dataInspection.idFuncionario || null,
        professionalId: this.dataInspection.idFuncionario || null,
        idResponsavelTecnico: this.dataInspection.idResponsavelTecnico || null,
        professionalName: this.dataInspection.professionalName,
        propertyId: this.dataInspection.idTalhao || null,
        property: this.dataInspection.propertyName,
        culturaSelecionada: firstDiagnosis.culturaSelecionada || null,
        cultureId: firstDiagnosis.culturaSelecionada || null,
        cultureName: cultures.join(", ").slice(0, 180),
        crop: cultures.join(", ").slice(0, 180),
        nomeTalhao: talhoes.join(", ").slice(0, 250),
        field: talhoes.join(", ").slice(0, 250),
        variedade: firstDiagnosis.variedade,
        estadioFenologico: firstDiagnosis.estadioFenologico,
        umidadeSolo: firstDiagnosis.umidadeSolo,
        mipSampling: firstDiagnosis.mipSampling,
        alvosPragas: firstDiagnosis.alvosPragas,
        alvosDoencas: firstDiagnosis.alvosDoencas,
        alvosDaninhas: firstDiagnosis.alvosDaninhas,
        date: this.dataInspection.data,
        dadosAdicionais: isAgendaMetadata(this.dataInspection.dadosAdicionais)
          ? this.dataInspection.dadosAdicionais
          : firstDiagnosis.dadosAdicionais,
        recomendacaoManejo: firstDiagnosis.recomendacaoManejo,
        recipeId: firstDiagnosis.recipeId || null,
        technicalNotes: firstDiagnosis.dadosAdicionais,
        recommendation: firstDiagnosis.recomendacaoManejo,
        attachments,
        status: concluded
          ? "Concluído"
          : ["Concluído", "Cancelado"].includes(this.dataInspection.status)
          ? this.dataInspection.status
          : "Em andamento",
        resolved: concluded
          ? this.conclusion.resolved
          : this.dataInspection.resolved === null ||
            this.dataInspection.resolved === undefined
          ? null
          : Boolean(Number(this.dataInspection.resolved)),
        followUpRequired: concluded
          ? this.conclusion.followUp
          : this.dataInspection.followUpRequired || false,
        followUpDate: concluded
          ? this.conclusion.followUp
            ? this.conclusion.followUpDate
            : null
          : this.dataInspection.followUpDate || null,
      };

      try {
        if (inspection.id) {
          await serviceInspection.update(inspection, newFiles);
        } else {
          inspection.id = await serviceInspection.save(inspection, newFiles);
          this.dataInspection.id = inspection.id;
        }
        this.dataInspection.status = inspection.status;

        const saved = await serviceInspection.findById(inspection.id);
        const synchronizedInspection = {
          ...this.dataInspection,
          ...saved,
          fotos: saved.attachments || [],
          pendingActions: saved.pendingActions || [],
        };
        delete synchronizedInspection.attachments;
        this.dataInspection = synchronizedInspection;
        this.initializeDiagnoses();
        this.$toast.open({
          message: concluded
            ? "Atendimento concluído e mantido no histórico"
            : "Atendimento salvo com sucesso",
          type: "success",
        });
        this.$emit("saved", saved);
        return saved;
      } catch (error) {
        this.$toast.open({
          message: "Não foi possível salvar a visita técnica",
          type: "error",
        });
        return null;
      }
    },

    addPendingAction() {
      this.dataInspection.pendingActions.push({
        id: `${Date.now()}-${Math.random()}`,
        description: "",
        responsible: "",
        deadline: "",
        status: "Pendente",
        completedAt: null,
        statusHistory: [{ status: "Pendente", date: new Date().toISOString() }],
      });
    },
    removePendingAction(index) {
      this.dataInspection.pendingActions.splice(index, 1);
    },
    registerPendingStatus(action) {
      action.statusHistory = action.statusHistory || [];
      action.statusHistory.push({
        status: action.status,
        date: new Date().toISOString(),
      });
      action.completedAt =
        action.status === "Resolvido" ? new Date().toISOString() : null;
    },
    formatAuditDate(value) {
      return value ? moment(value).format("DD/MM/YYYY HH:mm") : "";
    },
    openConclusion() {
      this.showConclusion = true;
    },
    setFollowUp(days) {
      this.conclusion.followUpDate = moment()
        .add(days, "days")
        .format("YYYY-MM-DD");
    },
    async concludeAttendance() {
      if (this.isConcluding || this.isSavingInspection) return;
      if (this.conclusion.followUp && !this.conclusion.followUpDate) {
        this.$toast.open({
          message: "Escolha a data do acompanhamento",
          type: "warning",
        });
        return;
      }
      this.isConcluding = true;
      try {
        const saved = await this.saveInspection(true);
        if (!saved) return;
        if (this.conclusion.followUp) {
          await apiSaveCommitment({
            type: "Retorno/acompanhamento",
            title: "Retorno do atendimento agronômico",
            date: this.conclusion.followUpDate,
            customerId: saved.customerId,
            customerName: saved.customerName || "Produtor vinculado",
            property: saved.property || "",
            crop: saved.crop || "",
            field: saved.field || "",
            responsible: saved.professionalName || "Responsável técnico",
            status: "Agendado",
          });
        }
        this.showConclusion = false;
        this.openRecipePrompt();
      } catch (error) {
        this.$toast.open({
          message: "Não foi possível concluir o atendimento",
          type: "error",
        });
      } finally {
        this.isConcluding = false;
      }
    },

    printInspection() {
      if (this.dataInspection.id) {
        serviceInspection.print(this.dataInspection.id);
      }
    },

    openRecipePrompt() {
      const activeCultureId = String(
        this.activeDiagnosis?.culturaSelecionada || ""
      );
      const activePendingCulture = this.remainingRecipeCultureOptions.find(
        (culture) => culture.id === activeCultureId
      );
      const nextCulture =
        activePendingCulture || this.remainingRecipeCultureOptions[0];

      if (!nextCulture) {
        this.$toast.open({
          message: "Todas as culturas desta inspeção já possuem receita.",
          type: "info",
        });
        return;
      }

      this.recipeCultureId = nextCulture.id;
      this.showRecipePrompt = true;
    },

    async startPrescription() {
      const savedInspection = await this.saveInspection(false);
      if (!savedInspection) return;

      const responsible = this.dataTechnicalManagers.find(
        (item) => item.id === this.dataInspection.idResponsavelTecnico
      );
      const prescription = buildPrescriptionDraftFromInspection({
        inspection: this.dataInspection,
        cultureId: this.recipeCultureId,
        responsible,
      });
      if (!prescription || this.selectedRecipeCulture?.hasRecipe) return;
      if (
        prescription.recommendationCount &&
        !prescription.draft.pendingInspectionProducts.length
      ) {
        this.$toast.open({
          message:
            "Esta cultura possui apenas produtos sem estoque. Não é possível gerar a receita até haver um produto disponível.",
          type: "warning",
        });
        return;
      }
      this.SET_DATA_SEARCH_FROM_SALE(prescription.draft);
      if (prescription.unavailableProductCount) {
        this.$toast.open({
          message: `${prescription.unavailableProductCount} produto(s) somente do Compêndio não serão incluídos na receita.`,
          type: "info",
        });
      }
      this.showRecipePrompt = false;
      this.$router.push({
        name: "receita",
        query: { origem: "inspecao" },
      });
    },

    async fillCustomerComboBoxWithPaginate(id, q, filterType, page) {
      try {
        return await serviceCustomer.fillComboBoxWithPaginate(
          id,
          q,
          filterType,
          page
        );
      } catch (error) {
        this.$toast.open({
          message: "Ocorreu um problema ao listar os clientes",
          type: "info",
        });
      }
    },

    openModals(idModal) {
      this.$bvModal.show(idModal);
    },

    async handleDataCustomers(event) {
      const preserveProperty =
        String(this.dataInspection.idCliente || "") === String(event?.id || "");
      this.dataInspection.idCliente = event?.id || "";
      this.dataInspection.customerName =
        event?.nome || event?.nomeCliente || "";
      await this.loadProperties(
        this.dataInspection.idCliente,
        preserveProperty
      );
    },

    handleCreatedNewCustomer() {
      this.createdNewCustomer = !this.createdNewCustomer;
    },
    async onlyForPesticides() {
      try {
        const result = await serviceProducts.onlyForPesticides();
        const products = Array.isArray(result) ? result : result?.data || [];
        this.pesticidesInStock = products.filter(
          (product) => Number(product.estoque) > 0
        );
      } catch (error) {
        this.pesticidesInStock = [];
        this.$toast.open({
          message: "Não foi possível carregar o estoque de defensivos",
          type: "warning",
        });
      }
    },
  },
  computed: {
    activeDiagnosis() {
      return (
        this.dataInspection.diagnosticos?.find(
          (item) => item.id === this.activeDiagnosisId
        ) || this.diagnosisPlaceholder
      );
    },
    /* TRANSCRIÇÃO EM STANDBY
    audioTranscriptionContext() {
      const diagnosis = this.activeDiagnosis;
      const terms = (targets) =>
        (targets || []).flatMap((target) =>
          [target.nome, target.nomeCientifico].filter(Boolean)
        );
      return {
        talhao: diagnosis.nomeTalhao || "",
        cultura: diagnosis.cultureName || "",
        variedade: diagnosis.variedade || "",
        estadioFenologico: diagnosis.estadioFenologico || "",
        umidadeSolo: diagnosis.umidadeSolo || "",
        pragas: terms(diagnosis.alvosPragas),
        doencas: terms(diagnosis.alvosDoencas),
        plantasDaninhas: terms(diagnosis.alvosDaninhas),
      };
    },
    */
    totalMipTargets() {
      return (
        this.activeDiagnosis.alvosPragas.length +
        this.activeDiagnosis.alvosDoencas.length +
        this.activeDiagnosis.alvosDaninhas.length
      );
    },
    searchableDiagnoses() {
      return (this.dataInspection.diagnosticos || []).filter(
        (diagnosis) =>
          diagnosis.culturaSelecionada &&
          this.diagnosisTargets(diagnosis).length
      );
    },
    recipeCultureOptions() {
      return inspectionPrescriptionCultureOptions(this.dataInspection);
    },
    remainingRecipeCultureOptions() {
      return this.recipeCultureOptions.filter((culture) => !culture.hasRecipe);
    },
    selectedRecipeCulture() {
      return this.recipeCultureOptions.find(
        (culture) => culture.id === String(this.recipeCultureId || "")
      );
    },
    suggestionsSubtitle() {
      return this.suggestionSource === "stock"
        ? "Somente produtos em estoque compatíveis com a cultura e o diagnóstico"
        : "Resultados do Compêndio Agronômico, inclusive produtos sem estoque";
    },
    pesticideSuggestionContext() {
      return (this.dataInspection.diagnosticos || [])
        .map((diagnosis) => {
          const targetKeys = this.diagnosisTargets(diagnosis).map(
            (target) => `${target.id}:${target.catalogId || ""}`
          );
          return `${diagnosis.id}|${
            diagnosis.culturaSelecionada
          }|${targetKeys.join(",")}`;
        })
        .join(";");
    },
    activeDiagnosisPhotos() {
      const firstDiagnosisId = this.dataInspection.diagnosticos?.[0]?.id;
      return (this.dataInspection.fotos || []).filter((photo) => {
        if (photo?.type === "audio") return false;
        const diagnosisId = photo?._diagnosisId || photo?.diagnosisId;
        return diagnosisId
          ? diagnosisId === this.activeDiagnosis.id
          : this.activeDiagnosis.id === firstDiagnosisId;
      });
    },
    activeDiagnosisAudios() {
      const firstDiagnosisId = this.dataInspection.diagnosticos?.[0]?.id;
      return (this.dataInspection.fotos || []).filter((audio) => {
        if (audio?.type !== "audio") return false;
        const diagnosisId = audio?.diagnosisId || audio?.diagnosticoId;
        return diagnosisId
          ? diagnosisId === this.activeDiagnosis.id
          : this.activeDiagnosis.id === firstDiagnosisId;
      });
    },
    ...mapState({
      dataSearchFromInspect: (state) => state.dataSearchFromInspect,
    }),
  },
  watch: {
    pesticideSuggestionContext(newContext, oldContext) {
      if (oldContext && newContext !== oldContext) {
        this.resetPesticideSuggestions();
      }
    },
  },
  async created() {
    if (
      this.dataSearchFromInspect &&
      Object.keys(this.dataSearchFromInspect).length
    ) {
      this.dataInspection = {
        ...this.dataInspection,
        ...this.dataSearchFromInspect,
        pendingActions: this.dataSearchFromInspect.pendingActions || [],
        fotos:
          this.dataSearchFromInspect.fotos ||
          this.dataSearchFromInspect.attachments ||
          [],
      };
    }
    this.initializeDiagnoses();
    await Promise.all([
      this.loadEmployees(),
      this.loadTechnicalManagers(),
      this.loadVehicles(),
    ]);
    if (
      this.dataSearchFromInspect &&
      Object.keys(this.dataSearchFromInspect).length
    ) {
      this.referenceModes.customer = this.dataInspection.idCliente
        ? "registered"
        : "manual";
      this.referenceModes.property = this.dataInspection.idTalhao
        ? "registered"
        : "manual";
      await this.loadProperties(this.dataInspection.idCliente, true);
      await this.loadTalhoes(this.dataInspection.idTalhao, true);
      this.dataInspection.diagnosticos.forEach((diagnosis) => {
        const matchingTalhao = this.dataTalhoes.find(
          (talhao) =>
            String(talhao.id) === String(diagnosis.talhaoId) ||
            String(talhao.nome || "").toLocaleLowerCase() ===
              String(diagnosis.nomeTalhao || "").toLocaleLowerCase()
        );
        if (matchingTalhao) {
          diagnosis.talhaoId = matchingTalhao.id;
          diagnosis.nomeTalhao = matchingTalhao.nome;
          diagnosis.talhaoMode = "registered";
        } else if (diagnosis.nomeTalhao) {
          diagnosis.talhaoMode = "manual";
        }
        diagnosis.cultureMode = diagnosis.culturaSelecionada
          ? "registered"
          : "manual";
      });
    }
    this.hydrateMipData();
    await this.onlyForPesticides();
  },
};
</script>

<style scoped>
.containerGeralInspection {
  width: min(1380px, calc(100% - 68px));
  margin: 1.3rem auto 3rem;
  font-family: "Poppins", sans-serif;
  color: #263238;
}

.titleHeader {
  margin-left: 0;
  border-left: 0;
  padding: 10px;
  width: 100%;
  background: linear-gradient(110deg, #edf7f4, #fff 65%);
  border: 1px solid #dce9e5;
  border-radius: 8px;
}
.eyebrow {
  font-size: 12px;
  letter-spacing: 1.4px;
  color: #006256;
  font-weight: 700;
  margin: 0 0 3px;
}

.titleMain {
  font-size: 17px;
  color: #1e293b;
  font-weight: 700;
  margin: 0;
}

.dashedSeparator {
  border-top: 1px dashed #cbd5e1;
  margin: 1rem 0;
}
.sectionDivider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 22px 0 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #dce7e4;
}
.sectionDivider > span {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 7px;
  background: #006256;
  color: white;
  font-size: 12px;
  font-weight: 700;
}
.sectionDivider > div {
  display: flex;
  flex-direction: column;
}
.sectionDivider b {
  font-size: 13px;
  color: #263238;
}
.sectionDivider small {
  font-size: 12px;
  color: #71817d;
}

.labelGps {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
  font-weight: 600;
}

.btnGps {
  height: 30px;
  background-color: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #334155;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btnGps:hover {
  background-color: #e2e8f0;
  border-color: #94a3b8;
}

.btnAIAssist {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(99, 102, 241, 0.2);
  transition: transform 0.1s ease;
}

.btnAIAssist:active {
  transform: scale(0.95);
}

.btnAIAssist:disabled {
  cursor: wait;
  opacity: 0.65;
}

.agrotoxicSuggestionsPanel {
  margin-top: 12px;
  background: #f8fafb;
  border: 1px solid #dce8e3;
  border-radius: 8px;
  padding: 14px 16px;
}

.suggestionsPanelHeader {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e8f0ec;
}

.suggestionsPanelHeader .obsGeneral {
  margin: 0;
  font-size: 13px;
}

.suggestionsPanelHeader .observacoes {
  margin: 2px 0 0;
  font-size: 11px;
}

.suggestionHeaderActions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.suggestionDiagnosisSection {
  margin-bottom: 14px;
  padding: 10px;
  border: 1px solid #dce8e3;
  border-radius: 7px;
  background: #fff;
}

.suggestionDiagnosisHeader {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
  padding-bottom: 7px;
  border-bottom: 1px solid #e8f0ec;
  color: #176b57;
  font-size: 12px;
}

.suggestionTargetEmpty {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 4px;
  color: #71817d;
  font-size: 11px;
}

.compendiumOnlyAvailability {
  color: #8a5b00;
  font-size: 9px;
  font-weight: 700;
}

.badgeCompendio {
  flex-shrink: 0;
  background: #e0f2e9;
  color: #176b57;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.badgeCompendio.badgeLoading {
  background: #fef3cd;
  color: #856404;
  animation: pulse 1.2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.suggestionsLoadingState {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 0;
  color: #687b75;
  font-size: 12px;
}

.loadingSpinner {
  width: 16px;
  height: 16px;
  border: 2px solid #dce8e3;
  border-top-color: #176b57;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.suggestionsSearchBar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.inputSearchAgro {
  flex: 1;
  font-size: 12px;
  font-family: "Poppins", sans-serif;
  padding: 6px 10px;
  border: 1px solid #dce8e3;
  border-radius: 5px;
  outline: none;
  background: #ffffff;
  color: #18332c;
  transition: border-color 0.15s ease;
}

.inputSearchAgro::placeholder {
  color: #94a3a0;
}

.inputSearchAgro:focus {
  border-color: #176b57;
}

.searchResultCount {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: #687b75;
  white-space: nowrap;
}

.suggestionsScrollArea {
  max-height: 380px;
  overflow-y: auto;
  padding-right: 4px;
}

.suggestionsScrollArea::-webkit-scrollbar {
  width: 5px;
}

.suggestionsScrollArea::-webkit-scrollbar-track {
  background: transparent;
}

.suggestionsScrollArea::-webkit-scrollbar-thumb {
  background: #c5d4ce;
  border-radius: 3px;
}

.suggestionGroup {
  margin-bottom: 12px;
}

.suggestionGroup:last-child {
  margin-bottom: 0;
}

.suggestionGroupHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  padding-bottom: 4px;
  border-bottom: 1px dashed #dce8e3;
}

.suggestionGroupTitle {
  display: flex;
  align-items: center;
  gap: 5px;
}

.suggestionTargetIcon {
  font-size: 13px;
}

.suggestionTargetName {
  font-size: 12px;
  font-weight: 700;
  color: #18332c;
}

.suggestionTargetSci {
  font-size: 10px;
  color: #687b75;
  font-style: italic;
}

.suggestionGroupCount {
  font-size: 10px;
  font-weight: 700;
  color: #687b75;
  background: #e8f0ec;
  padding: 1px 6px;
  border-radius: 8px;
}

.suggestionGrid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

@media (max-width: 1100px) {
  .suggestionGrid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 700px) {
  .suggestionGrid {
    grid-template-columns: 1fr;
  }
}

.suggestionCard {
  background: #ffffff;
  border: 1px solid #e8f0ec;
  border-radius: 6px;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.suggestionCard:hover {
  border-color: #176b57;
  box-shadow: 0 1px 3px rgba(23, 107, 87, 0.08);
}

.suggestionCardHeader {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.agrotoxicoReg {
  font-size: 10px;
  font-weight: 700;
  color: #176b57;
  background: #e0f2e9;
  padding: 2px 5px;
  border-radius: 3px;
  align-self: flex-start;
  letter-spacing: 0.2px;
}

.agrotoxicoName {
  font-size: 10px;
  font-weight: 500;
  color: #687b75;
  line-height: 1.3;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}

.stockAvailability {
  align-self: flex-start;
  color: #176b57;
  font-size: 9px;
  font-weight: 700;
}

.suggestionCardDetails {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.detailChip {
  font-size: 9px;
  font-weight: 600;
  color: #176b57;
  background: #e0f2e9;
  padding: 2px 5px;
  border-radius: 3px;
  white-space: nowrap;
}

.suggestionCardFooter {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-top: 4px;
  border-top: 1px solid #f0f4f2;
}

.inputDateCompact {
  flex: 1;
  font-size: 10px;
  font-family: "Poppins", sans-serif;
  padding: 3px 4px;
  border: 1px solid #dce8e3;
  border-radius: 3px;
  color: #18332c;
  background: #ffffff;
  outline: none;
  min-width: 0;
}

.inputDateCompact:focus {
  border-color: #176b57;
}

.btnAddSuggestion {
  flex-shrink: 0;
  background: #176b57;
  border: none;
  color: #ffffff;
  font-size: 9px;
  font-weight: 700;
  padding: 4px 6px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s ease;
  white-space: nowrap;
}

.btnAddSuggestion:hover:not(:disabled) {
  background: #105242;
}

.btnAddSuggestion:active:not(:disabled) {
  transform: scale(0.95);
}

.btnAddSuggestion:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.suggestionsEmptyState {
  padding: 12px 0;
  text-align: center;
  color: #687b75;
  font-size: 12px;
}

.suggestionsEmptyState p {
  margin: 0 0 10px;
}

.suggestionsEmptyState small {
  display: block;
  margin: -4px 0 10px;
  color: #8a5b00;
}

.btnSearchCompendium {
  padding: 6px 12px;
  border: 1px solid #176b57;
  border-radius: 5px;
  color: #176b57;
  background: #ffffff;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}

.btnSearchCompendium:hover {
  color: #ffffff;
  background: #176b57;
}

.inputFileAgro {
  font-size: 12px;
  color: #64748b;
}

.inputFileAgro::file-selector-button {
  background-color: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 6px 12px;
  margin-right: 10px;
  color: #334155;
  font-weight: 600;
  cursor: pointer;
}

.previewContainer {
  display: flex;
  gap: 10px;
}

.previewImg {
  display: flex;
  background-color: #e2e8f0;
  padding: 10px;
  border-radius: 6px;
  gap: 10px;
  font-size: 12px;
  color: #475569;
  flex-direction: column;
  align-items: center;
  position: relative;
}
.imgBox {
  width: 150px;
  height: 100px;
}
.photoCoordBtn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid #cbd5e1;
  background: #fff;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
  opacity: 0.7;
  transition: opacity 0.15s;
}
.photoCoordBtn:hover,
.photoCoordBtn.active {
  opacity: 1;
  border-color: #006256;
  background: #f0f7f5;
}
.photoCoordBadge {
  display: block;
  font-size: 9px;
  color: #006256;
  font-family: monospace;
  background: #f0f7f5;
  padding: 1px 5px;
  border-radius: 3px;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dynamicGridAgro {
  display: flex;
  gap: 10px;
  padding: 0;
  margin: 10px;
}

.agroCard {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0;
  box-shadow: 0 3px 10px rgb(26 71 61 / 5%);
  flex: 1;
}

.cardAgroHeader {
  padding: 10px 16px;
  font-weight: 600;
  font-size: 12px;
  border-radius: 8px 8px 0 0;
  color: #ffffff;
}

.pragasTitle {
  background-color: #39554e;
}
.doencasTitle {
  background-color: #006256;
}
.daninhasTitle {
  background-color: #718c4a;
}

.cardAgroBody {
  padding: 10px;
}

.agroCheckItem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
}

.agroCheckItemColumn {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 5px 0;
  border-bottom: 1px solid #f1f5f9;
}

.labelAlvo {
  font-size: 12px;
  font-weight: 500;
  color: #334155;
}

.counterAction {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f8fafc;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  padding: 2px;
}

.btnCounter {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  font-weight: bold;
  color: #334155;
  cursor: pointer;
  transition: all 0.2s;
}

.btnCounter:hover {
  background: #006256;
  color: white;
}

.valueCounter {
  font-size: 12px;
  font-weight: 600;
  min-width: 75px;
  text-align: center;
  color: #0f172a;
}

.radio-input-agro {
  display: flex;
  background-color: #f1f5f9;
  border-radius: 6px;
  padding: 3px;
  border: 1px solid #e2e8f0;
}

.radio-input-agro label {
  flex: 1;
  text-align: center;
  margin: 0;
  cursor: pointer;
}

.radio-input-agro label input {
  display: none;
}

.radio-input-agro label span {
  display: block;
  padding: 3px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 4px;
  color: #64748b;
  transition: all 0.2s;
}

.radio-input-agro label input:checked + span {
  background-color: #0f766e;
  color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.custom-checkbox {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.checkboxLabelText {
  font-size: 12px;
  margin-left: 6px;
  color: #64748b;
  font-weight: 600;
}

.custom-checkbox input[type="checkbox"]:checked ~ .checkboxLabelText {
  color: #b45309;
}

.inputTextAreaAgro {
  width: 100%;
  border: 1px solid #cbd5e1;
  outline: none;
  background-color: #ffffff;
  color: #334155;
  border-radius: 6px;
  padding: 10px;
  font-size: 12px;
  resize: none;
  transition: all 0.2s;
}
.contentItens {
  margin-bottom: 1.5rem !important;
}
.visitContext {
  display: grid !important;
  grid-template-columns: 1fr 2fr 1fr;
  gap: 15px;
}
.visitContext label {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}
.visitContext input {
  height: 30px;
  border: 1px solid #cbd5e1;
  border-radius: 5px;
  padding: 5px;
  font-size: 12px;
}

.inputTextAreaAgro:focus,
.inputTextAreaAgro:hover {
  border-color: #006256;
  background-color: #f8fafc;
}

.obsGeneral {
  font-size: 13px;
  color: #1e293b;
  font-weight: 700;
  margin: 0;
}

.observacoes {
  font-size: 12px;
  color: #64748b;
  font-style: italic;
  margin: 0;
}

.contentButtonsActions {
  display: flex;
  justify-content: space-between;
}

.btnsBudget {
  height: 30px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: ease all 0.2s;
}

.btnBack {
  background-color: #ffffff;
  border: 1px solid #cbd5e1;
  color: #64748b;
}

.btnBack:hover {
  background-color: #f1f5f9;
}

.btnConfirmAgro {
  background-color: #006256;
  color: white;
  box-shadow: 0 4px 6px -1px rgba(0, 98, 86, 0.2);
}

.btnConfirmAgro:hover {
  background-color: #004d43;
  box-shadow: 0 4px 12px -1px rgba(0, 98, 86, 0.4);
}
.workflowPanel {
  border: 1px solid #dce7e4;
  border-radius: 9px;
  padding: 16px !important;
  border-color: var(--agro-border);
  background: var(--agro-soft);
}
.workflowHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.btnSecondary,
.followUpOptions button {
  border: 1px solid #006256;
  color: #006256;
  background: white;
  border-radius: 6px;
  padding: 5px 11px;
  font-size: 12px;
  font-weight: 600;
}
.pendingRow {
  display: grid;
  grid-template-columns: 2fr 1fr 140px 150px 30px;
  gap: 8px;
  margin-top: 10px;
  align-items: center;
}
.pendingRow input,
.pendingRow select,
.recipeInput,
.conclusionQuestion select {
  height: 36px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 6px;
  font-size: 12px;
  background: white;
}
.pendingRow small {
  grid-column: 1/-1;
}
.removeAction {
  border: 0;
  background: #feeceb;
  color: #b42318;
  border-radius: 6px;
  height: 32px;
}
.recipeInput {
  width: 100%;
  margin-top: 10px;
}
.historyItem {
  display: flex;
  flex-direction: column;
  border-left: 3px solid #006256;
  padding: 7px 10px;
  margin-top: 8px;
  font-size: 12px;
  background: white;
  border-radius: 0 5px 5px 0;
}
.conclusionQuestion {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 12px;
}
.followUpOptions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.concludeButton {
  width: 100%;
  margin-top: 18px;
}
.contentButtonsActions {
  gap: 12px;
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid #dce7e4;
}

.btnConfirmAgro {
  box-shadow: none;
}
.btnBack {
  color: #455a55;
}

.containerGeralInspection {
  --agro-primary: #176b57;
  --agro-primary-dark: #105242;
  --agro-ink: #18332c;
  --agro-muted: #687b75;
  --agro-border: #dce8e3;
  --agro-surface: #ffffff;
  --agro-soft: #f4f8f6;
  width: min(1280px, calc(100% - 48px));
  color: var(--agro-ink);
}

.vs__dropdown-toggle {
  height: 35px !important;
}
.pageHeader {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
  padding: 15px;
  overflow: hidden;
  position: relative;
  background: linear-gradient(120deg, #edf7f2 0%, #ffffff 72%);
  border: 1px solid #d7e8e1;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(27, 76, 63, 0.06);
}

.pageHeader::after {
  content: "";
  position: absolute;
  width: 170px;
  height: 170px;
  right: -70px;
  top: -105px;
  border-radius: 50%;
  background: rgba(23, 107, 87, 0.07);
}

.pageHeaderIcon {
  display: grid;
  place-items: center;
  flex: 0 0 46px;
  width: 46px;
  height: 46px;
  border-radius: 12px;
  font-size: 22px;
  background: var(--agro-primary);
  box-shadow: 0 7px 14px rgba(23, 107, 87, 0.2);
}

.titleHeader {
  padding: 0;
  border: 0;
  background: transparent;
  min-width: 0;
}

.eyebrow {
  margin-bottom: 4px;
  color: var(--agro-primary);
  font-size: 12px;
  letter-spacing: 1.35px;
}

.titleMain {
  color: var(--agro-ink);
  font-size: 22px;
  line-height: 1.25;
}

.statusBadge {
  flex: 0 0 auto;
  z-index: 1;
  margin-left: auto;
  padding: 7px 12px;
  border: 1px solid #b8dace;
  border-radius: 999px;
  color: var(--agro-primary-dark);
  background: #e9f6f1;
  font-size: 12px;
  font-weight: 700;
}

.formSection,
.contentChecklist,
.contentItens {
  padding: 22px;
  border: 1px solid var(--agro-border);
  border-radius: 12px;
  background: var(--agro-surface);
  box-shadow: 0 5px 18px rgba(31, 66, 57, 0.045);
}

.sectionHeading,
.sectionDivider {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sectionHeading {
  margin-bottom: 22px;
  padding-bottom: 14px;
  border-bottom: 1px solid #e7efec;
}

.sectionNumber,
.sectionDivider > span {
  display: grid;
  place-items: center;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  color: #fff;
  background: var(--agro-primary);
  font-size: 12px;
  font-weight: 700;
}

.sectionHeading h2 {
  margin: 0;
  color: var(--agro-ink);
  font-size: 15px;
  font-weight: 700;
}

.sectionHeading p {
  margin: 2px 0 0;
  color: var(--agro-muted);
  font-size: 12px;
}

.visitGrid {
  display: grid;
  gap: 18px 14px;
  align-items: end;
  margin-bottom: 23px;
}

.visitGridPrimary {
  grid-template-columns: 1.05fr 0.8fr 0.9fr 0.95fr 2.4fr;
}

.fieldVisitTeam {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.visitGridLocation {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.visitGridCrop {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.diagnosisContextGrid {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.activeDiagnosisBanner {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 4px 0 14px;
  padding: 9px 12px;
  border-left: 4px solid var(--agro-primary);
  border-radius: 0 7px 7px 0;
  color: var(--agro-ink);
  background: var(--agro-soft);
  font-size: 12px;
}

.activeDiagnosisBanner span {
  color: var(--agro-muted);
  text-transform: uppercase;
}

.visitGrid > * {
  width: 100% !important;
  min-width: 0;
}

.referenceField {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.referenceMode {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-height: 18px;
}

.referenceMode > span {
  overflow: hidden;
  color: #526660;
  font-size: 12px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.referenceMode button {
  flex: 0 0 auto;
  padding: 1px 0;
  border: 0;
  border-bottom: 1px solid currentColor;
  color: var(--agro-primary);
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.referenceField > .input-group,
.referenceField > .selectPlus,
.referenceField > div:not(.referenceMode) {
  width: 100%;
}

.visitGrid ::v-deep .borderInput {
  height: 35px;
  padding-left: 10px;
  border-color: #cddbd6;
  border-radius: 7px;
  font-size: 12px !important;
}

.visitGrid ::v-deep .iconSelect {
  width: 35px;
  height: 35px;
  border-radius: 0 7px 7px 0;
  background: var(--agro-primary);
}

.visitGrid ::v-deep .vs__dropdown-toggle {
  min-height: 40px;
  border-color: #cddbd6;
  border-radius: 7px;
}

.visitGrid ::v-deep .vs__selected,
.visitGrid ::v-deep .vs__search {
  font-size: 12px;
}

.visitGrid ::v-deep .labelFixed,
.visitGrid ::v-deep .fixedSelect {
  font-size: 13px;
  bottom: 25px;
  color: #526660;
  background: white;
  padding: 0;
  left: -13px !important;
}
.visitGrid ::v-deep .floatLabel {
  font-size: 10.5px;
  top: -14px;
  color: #526660;
  background: white;
  padding: 0 3px;
  left: 5px !important;
}
.labelFixed {
  left: -20px !important;
}
.fieldLocation {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.fieldLabel {
  padding-left: 2px;
  color: #526660;
  font-size: 12px;
  font-weight: 600;
}

.btnGps {
  height: 35px;
  border-color: #bcd4cc;
  border-radius: 7px;
  color: var(--agro-primary-dark);
  background: #f3f9f6;
}

.btnGps:hover {
  border-color: var(--agro-primary);
  background: #e9f5f0;
}

.visitContext {
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr);
  margin-top: 2px;
  align-items: end;
}
.borderInput {
  font-size: 14px;
  height: 30px;
  border: 1px solid #b2b2b2;
  border-radius: 5px;
  outline: none;
  transition: ease-in 0.1s all;
  padding-left: 5px;
  padding-right: 4px;
  color: #5c5855;
}

.borderInput:focus {
  border: 1px solid #505050;
}

.visitContext label {
  gap: 6px;
  margin: 0;
  color: #526660;
}

.visitContext input {
  height: 35px;
  padding: 8px 10px;
  border-color: #cddbd6;
  border-radius: 7px;
  outline: none;
}

.visitContext input:focus {
  border-color: var(--agro-primary);
  box-shadow: 0 0 0 3px rgba(23, 107, 87, 0.08);
}

.sectionDivider {
  margin: 24px 0 12px;
  padding: 0 2px;
  border: 0;
}

.sectionDivider b {
  color: var(--agro-ink);
  font-size: 14px;
}

.sectionDivider small {
  color: var(--agro-muted);
  font-size: 12px;
}

.contentChecklist .sectionTitleAgro {
  margin-bottom: 0;
}

.mipHeading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.mipTargetCount {
  flex: 0 0 auto;
  padding: 6px 10px;
  border: 1px solid #cfe1da;
  border-radius: 999px;
  color: var(--agro-primary-dark);
  background: #f1f8f5;
  font-size: 12px;
  font-weight: 700;
}

.samplingPlan {
  display: grid;
  grid-template-columns: 1.5fr 0.7fr 0.7fr 1fr;
  gap: 12px;
  margin-bottom: 18px;
  padding: 16px;
  border: 1px solid #d9e7e2;
  border-radius: 9px;
  background: #f6faf8;
}

.samplingPlan label,
.technicalMeasure,
.weedAssessment > label:last-child {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 0;
}

.samplingPlan label > span,
.technicalMeasure > span,
.weedAssessment label > span {
  color: #526660;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.15px;
}

.samplingPlan input,
.samplingPlan select {
  width: 100%;
  height: 38px;
  padding: 7px 9px;
  border: 1px solid #c9d9d3;
  border-radius: 6px;
  outline: none;
  color: #263b35;
  background: #fff;
  font-size: 12px;
}

.samplingPlan input:focus,
.samplingPlan select:focus {
  border-color: var(--agro-primary);
  box-shadow: 0 0 0 3px rgba(23, 107, 87, 0.07);
}

.samplingNotes {
  grid-column: 1 / -1;
}

.dynamicGridAgro {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin: 0;
}

.agroCard {
  width: auto;
  min-width: 0;
  border-color: var(--agro-border);
  border-radius: 10px;
  box-shadow: none;
}

.cardAgroHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 13px 14px;
  border-radius: 9px 9px 0 0;
  font-size: 12px;
}

.cardAgroHeader > div {
  display: flex;
  align-items: center;
  gap: 7px;
}

.cardAgroHeader small {
  color: rgba(255, 255, 255, 0.72);
  font-size: 12px;
  font-weight: 500;
  text-align: right;
}

.cardAgroIcon {
  font-size: 15px;
}

.addTargetBox {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 34px auto;
  gap: 6px;
  padding: 11px 12px;
  border-bottom: 1px solid #e3ece8;
  background: #f7faf9;
}

.addTargetBox select,
.addTargetBox button,
.customTargetForm select,
.customTargetForm button,
.targetObservation,
.weedAssessment input {
  height: 34px;
  border: 1px solid #cbdad5;
  border-radius: 6px;
  outline: none;
  font-size: 12px;
}

.addTargetBox select,
.customTargetForm select,
.targetObservation,
.weedAssessment input {
  min-width: 0;
  padding: 6px 8px;
  color: #344b44;
  background: #fff;
}

.addTargetBox button,
.customTargetForm button {
  padding: 0 9px;
  border-color: var(--agro-primary);
  color: #fff;
  background: var(--agro-primary);
  font-weight: 700;
  cursor: pointer;
}

.addTargetBox .btnCustomTarget {
  border-color: #b9d1c9;
  color: var(--agro-primary);
  background: #fff;
}

.customTargetForm {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 6px;
  padding: 10px 12px;
  border-bottom: 1px solid #e3ece8;
  background: #edf6f2;
}

.cardAgroBody {
  padding: 0 14px 8px;
}

.cardAgroBodyScrollable {
  max-height: 456px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  scrollbar-color: #91b3a7 #edf4f1;
  scrollbar-width: thin;
}

.cardAgroBodyScrollable::-webkit-scrollbar {
  width: 7px;
}

.cardAgroBodyScrollable::-webkit-scrollbar-track {
  border-radius: 999px;
  background: #edf4f1;
}

.cardAgroBodyScrollable::-webkit-scrollbar-thumb {
  border: 2px solid #edf4f1;
  border-radius: 999px;
  background: #91b3a7;
}

.cardAgroBodyScrollable::-webkit-scrollbar-thumb:hover {
  background: var(--agro-primary);
}

.mipTargetItem {
  display: flex;
  flex-direction: column;
  gap: 9px;
  padding: 14px 0;
  border-bottom: 1px solid #e8efed;
}

.mipTargetItem:last-child {
  border-bottom: 0;
}

.targetTitleRow {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.targetTitleRow > div {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.targetTitleRow small {
  overflow: hidden;
  margin-top: 2px;
  color: #83928d;
  font-size: 12px;
  font-style: italic;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.btnRemoveTarget {
  display: grid;
  place-items: center;
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 5px;
  color: #a7443b;
  background: #fff1ef;
  font-size: 16px;
  cursor: pointer;
}

.technicalMeasure .counterAction {
  display: grid;
  grid-template-columns: 30px 62px minmax(75px, 1fr) 30px;
  gap: 4px;
  padding: 3px;
}

.counterAction input {
  width: 100%;
  height: 28px;
  padding: 2px 4px;
  border: 0;
  outline: none;
  color: #20352f;
  background: transparent;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
}

.counterAction > small {
  align-self: center;
  overflow: hidden;
  color: #667c75;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.targetObservation {
  width: 100%;
  background: #fbfdfc;
}

.weedAssessment {
  display: grid;
  grid-template-columns: 1fr 105px;
  gap: 10px;
  align-items: end;
}

.weedAssessment .custom-checkbox {
  min-height: 34px;
  margin: 0;
}

.weedAssessment input[type="number"] {
  width: 100%;
}

.weedAssessment input:disabled {
  color: #9ba8a4;
  background: #eef2f0;
}

.emptyTargetState {
  margin: 14px 0 6px;
  padding: 14px 10px;
  border: 1px dashed #c9d8d3;
  border-radius: 6px;
  color: #788984;
  background: #fafcfb;
  font-size: 12px;
  text-align: center;
}

.fieldHeading,
.fieldHeadingCopy {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 7px;
}

.fieldHeading {
  margin-bottom: 9px;
}

.fieldHeadingWithAction {
  justify-content: space-between;
  align-items: center;
}

.contentItens > .row {
  width: 100%;
  margin-right: 0;
  margin-left: 0;
  padding: 0;
}

.contentItens > .row > [class*="col-"] {
  padding-right: 0;
  padding-left: 0;
}

.inputFileAgro {
  width: 100%;
  padding: 12px;
  border: 1px dashed #b9cec7;
  border-radius: 8px;
  background: var(--agro-soft);
}

.inputFileAgro::file-selector-button {
  padding: 8px 14px;
  border: 1px solid #bcd4cc;
  border-radius: 6px;
  color: var(--agro-primary-dark);
  background: #fff;
}

.previewContainer {
  flex-wrap: wrap;
}

.inputTextAreaAgro {
  min-height: 92px;
  padding: 12px;
  border-color: #cddbd6;
  border-radius: 8px;
  line-height: 1.55;
  resize: vertical;
}

.workflowHeader {
  gap: 18px;
}

.btnSecondary,
.followUpOptions button {
  flex: 0 0 auto;
  padding: 5px 12px;
  border-color: var(--agro-primary);
  color: var(--agro-primary);
  cursor: pointer;
}

.emptyPendingState {
  margin-top: 14px;
  padding: 14px;
  border: 1px dashed #c6d9d2;
  border-radius: 7px;
  color: var(--agro-muted);
  background: rgba(255, 255, 255, 0.68);
  font-size: 12px;
  text-align: center;
}

.pendingRow {
  grid-template-columns: minmax(220px, 2fr) minmax(150px, 1fr) 140px 150px 36px;
  gap: 10px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #dce8e3;
  align-items: end;
}

.pendingRow label {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 0;
  color: #526660;
  font-size: 12px;
  font-weight: 600;
}

.pendingRow input,
.pendingRow select,
.recipeInput,
.conclusionQuestion select {
  height: 35px;
  padding: 8px 10px;
  border-color: #cddbd6;
  border-radius: 7px;
}

.removeAction {
  height: 35px;
  font-size: 20px;
  cursor: pointer;
}

.historyItem {
  padding: 10px 12px;
  font-size: 12px;
}

.contentButtonsActions {
  align-items: center;
  margin-top: 22px;
  padding-top: 20px !important;
}

.primaryActions {
  display: flex;
  gap: 10px;
  margin-left: auto;
}

.btnSave {
  border: 1px solid var(--agro-primary);
  color: var(--agro-primary);
  background: #fff;
}

.btnPrintInspection {
  border: 1px solid #5d766e;
  color: #405b53;
  background: #f7faf9;
}

.recipePromptText {
  margin-bottom: 18px;
  color: var(--agro-ink);
  font-size: 13px;
  line-height: 1.5;
}

.recipePromptActions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.recipePromptActions .btnsBudget {
  min-width: 120px;
  padding: 0 14px;
}

@media (max-width: 1100px) {
  .samplingPlan {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .visitGridPrimary {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .fieldVisitTeam {
    grid-column: 1 / -1;
  }

  .visitGridLocation {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .dynamicGridAgro {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .agroCard:last-child {
    grid-column: 1 / -1;
  }

  .pendingRow {
    grid-template-columns: 2fr 1fr 140px 36px;
  }

  .pendingRow label:nth-of-type(4) {
    grid-column: 1 / 4;
  }
}

@media (max-width: 900px) {
  .containerGeralInspection {
    width: calc(100% - 24px);
  }

  .visitGridPrimary,
  .visitGridCrop {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .fieldVisitTeam {
    grid-column: 1 / -1;
  }

  .pendingRow,
  .visitContext {
    grid-template-columns: 1fr;
  }

  .pendingRow label:nth-of-type(4) {
    grid-column: auto;
  }

  .pendingRow small {
    grid-column: auto;
  }

  .dynamicGridAgro {
    grid-template-columns: 1fr;
  }

  .agroCard:last-child {
    grid-column: auto;
  }

  .contentButtonsActions {
    flex-wrap: wrap;
  }

  .primaryActions {
    width: 100%;
    margin-left: 0;
  }

  .contentButtonsActions button {
    width: 100% !important;
  }
}

@media (max-width: 600px) {
  .containerGeralInspection {
    width: calc(100% - 16px);
    margin-top: 8px;
  }

  .pageHeader {
    align-items: flex-start;
    padding: 18px;
  }

  .pageHeaderIcon {
    display: none;
  }

  .statusBadge {
    position: absolute;
    top: 14px;
    right: 14px;
  }

  .titleHeader {
    padding-right: 80px;
  }

  .titleMain {
    font-size: 18px;
  }

  .formSection,
  .contentChecklist,
  .contentItens {
    padding: 16px;
  }

  .mipHeading {
    align-items: flex-start;
    flex-direction: column;
  }

  .samplingPlan {
    grid-template-columns: 1fr;
    padding: 12px;
  }

  .samplingNotes {
    grid-column: auto;
  }

  .addTargetBox {
    grid-template-columns: minmax(0, 1fr) 34px;
  }

  .addTargetBox .btnCustomTarget {
    grid-column: 1 / -1;
  }

  .customTargetForm {
    grid-template-columns: 1fr;
  }

  .visitGridPrimary,
  .visitGridLocation,
  .visitGridCrop {
    grid-template-columns: 1fr;
  }

  .fieldVisitTeam {
    grid-template-columns: 1fr;
  }

  .fieldProfessional,
  .fieldLocation {
    grid-column: auto;
  }

  .fieldHeadingWithAction,
  .workflowHeader {
    align-items: stretch;
    flex-direction: column;
  }

  .btnAIAssist,
  .btnSecondary {
    width: 100%;
  }

  .primaryActions {
    flex-direction: column;
  }

  .followUpOptions {
    flex-wrap: wrap;
  }
}
.recommendedProductsPanel {
  margin-top: 12px;
  padding: 12px 14px;
  border: 1px solid #b8dfd3;
  border-radius: 8px;
  background: #f1faf7;
}

.recommendedProductsHeader,
.recommendedProductRow {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.recommendedProductsHeader {
  align-items: flex-start;
  margin-bottom: 8px;
}

.recommendedProductsCount {
  white-space: nowrap;
  color: #006256;
  font-size: 11px;
  font-weight: 700;
}

.recommendedProductRow {
  align-items: center;
  padding: 9px 0;
  border-top: 1px solid #dbece6;
}

.recommendedProductRow > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  font-size: 12px;
}

.recommendedProductRow span,
.recommendedProductRow small {
  color: #62736e;
}

.removeRecommendedProduct {
  border: 0;
  background: transparent;
  color: #b42318;
  font-size: 11px;
  font-weight: 600;
}

.stockProductSelector {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 8px 0;
  color: #52635e;
  font-size: 10px;
  font-weight: 600;
}

.stockProductSelector select {
  width: 100%;
  min-height: 30px;
  border: 1px solid #cbdad5;
  border-radius: 5px;
  background: white;
  color: #263238;
  font-size: 11px;
}

.outOfStockNotice {
  margin: 8px 0;
  color: #9a6700;
  font-size: 10px;
}
</style>
